/**
 * Finance — Vendors, Bills, Payments (Phase 2).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import AdvancedTable from "@/components/table/AdvancedTable";
import { Truck, Receipt, IndianRupee, Plus, Users2, Paperclip, Camera, FileText, X, Loader2, Upload, Download, Mail, Send, UserPlus, Repeat, LineChart, AlertTriangle, Tag, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { apiErrorMessage } from "@/lib/apiError";
import { clientsLookup } from "@/lib/lookupCache";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

// Nov 2026 · Lightweight inline SVG sparkline (no recharts overhead
// per row — this renders 100s of times on the Vendors list).
// Draws a smooth line + a last-dot marker so drift pops at a glance.
function Sparkline({ values = [], width = 72, height = 20, stroke = "currentColor" }) {
  const vals = Array.isArray(values) ? values : [];
  const n = vals.length;
  if (n === 0 || vals.every((v) => !v)) {
    return <span className="text-[10px] text-muted-foreground/60">—</span>;
  }
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const span = Math.max(max - min, 1);
  const pad = 1;
  const stepX = (width - pad * 2) / Math.max(n - 1, 1);
  const pts = vals.map((v, i) => {
    const x = pad + i * stepX;
    const y = height - pad - ((v - min) / span) * (height - pad * 2);
    return [x, y];
  });
  const d = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const [lx, ly] = pts[pts.length - 1];
  return (
    <svg width={width} height={height} className="text-emerald-600 shrink-0" aria-label="12-month spend trend">
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lx} cy={ly} r="1.8" fill={stroke} />
    </svg>
  );
}


// Feb 2026 · Client-side CSV export. Uses the SAME canonical column
// headers accepted by the bulk-import endpoint so admins can round-
// trip an edit through Excel and re-import cleanly.
const VENDOR_CSV_HEADERS = [
  "name", "legal_name", "gstin", "pan",
  "contact_email", "contact_phone",
  "address_line1", "address_line2", "city", "state",
  "state_code", "pincode", "country",
  "default_tds_section", "default_tds_rate", "payment_terms_days",
];
function _csvCell(value) {
  const s = value === undefined || value === null ? "" : String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
function downloadVendorsCsv(vendors) {
  const rows = [VENDOR_CSV_HEADERS.join(",")];
  for (const v of vendors) {
    rows.push(VENDOR_CSV_HEADERS.map((h) => _csvCell(v[h])).join(","));
  }
  // BOM for Excel to auto-detect UTF-8.
  const blob = new Blob(["\ufeff" + rows.join("\r\n")],
    { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `vendors-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const fmt = (n, ccy = "INR") => {
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: ccy, maximumFractionDigits: 2 }).format(Number(n || 0));
  } catch { return `${ccy} ${Number(n || 0).toFixed(2)}`; }
};

export default function FinanceVendors() {
  const { activeWorkspaceId } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "vendors");
  useEffect(() => { setSp((p) => { const n = new URLSearchParams(p); n.set("tab", tab); return n; }, { replace: true }); }, [tab, setSp]);
  const wsId = activeWorkspaceId;

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="finance-vendors-page">
      <PageHeader title="Vendors & Bills" subtitle="Vendor management, purchase bills, payments — with GST + TDS auto-journalization." icon={Truck} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none flex-wrap h-auto bg-transparent p-0 border-b border-border w-full justify-start gap-1">
          <TabsTrigger value="ap-dashboard" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="fv-tab-ap-dashboard"><IndianRupee className="w-4 h-4 mr-2" />AP Dashboard</TabsTrigger>
          <TabsTrigger value="vendors" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="fv-tab-vendors"><Users2 className="w-4 h-4 mr-2" />Vendors</TabsTrigger>
          <TabsTrigger value="bills" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="fv-tab-bills"><Receipt className="w-4 h-4 mr-2" />Bills</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="fv-tab-payments"><IndianRupee className="w-4 h-4 mr-2" />Payments</TabsTrigger>
          <TabsTrigger value="recurring" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="fv-tab-recurring"><Repeat className="w-4 h-4 mr-2" />Recurring</TabsTrigger>
          <TabsTrigger value="projections" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="fv-tab-projections"><LineChart className="w-4 h-4 mr-2" />Projections</TabsTrigger>
          <TabsTrigger value="settings" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="fv-tab-settings"><Tag className="w-4 h-4 mr-2" />Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="ap-dashboard" className="mt-6"><ApDashboardTab wsId={wsId} /></TabsContent>
        <TabsContent value="vendors" className="mt-6"><VendorsTab wsId={wsId} /></TabsContent>
        <TabsContent value="bills" className="mt-6"><BillsTab wsId={wsId} /></TabsContent>
        <TabsContent value="payments" className="mt-6"><PaymentsTab wsId={wsId} /></TabsContent>
        <TabsContent value="recurring" className="mt-6"><RecurringTab wsId={wsId} /></TabsContent>
        <TabsContent value="projections" className="mt-6"><ProjectionsTab wsId={wsId} /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsTab wsId={wsId} /></TabsContent>
      </Tabs>
    </div>
  );
}

// Feb 2026 R284 · Authenticated Excel download. `window.location.href`
// drops the Authorization header (bearer token lives in localStorage,
// not a cookie), which is why every "template" and "export.xlsx"
// button was silently 401'ing on prod. axios auto-attaches the token;
// we then trigger a save-as via a temporary object-url.
// Shared between the Vendors tab, Bills tab, and per-vendor statement.
async function downloadXlsxAuth(path, filename, onErr) {
  try {
    const { data } = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) { (onErr || ((msg) => toast.error(msg)))(apiErrorMessage(e, "Download failed")); }
}

function VendorsTab({ wsId }) {
  const [vendors, setVendors] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} for new | vendor obj for edit
  const [importing, setImporting] = useState(false);
  // Nov 2026 · Per-vendor last-12-month spend trend (series + total)
  // keyed by vendor_id. Rendered as an inline SVG sparkline.
  const [spend, setSpend] = useState({ months: [], vendors: {} });
  const load = () => api.get(`/finance/workspaces/${wsId}/vendors`).then((r) => setVendors(r.data.vendors || []));
  useEffect(() => { if (wsId) load(); }, [wsId]);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/finance/workspaces/${wsId}/vendors/spend-trends`, { params: { months: 12 } })
       .then((r) => setSpend(r.data || { months: [], vendors: {} }))
       .catch(() => {});
  }, [wsId]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm text-muted-foreground">{vendors.length} vendors</div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="sm:size-default" onClick={() => downloadVendorsCsv(vendors)} disabled={vendors.length === 0} data-testid="vendor-export-btn">
            <Download className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Export CSV</span><span className="sm:hidden">CSV</span>
          </Button>
          {/* Feb 2026 · Excel round-trip. Same schema on the export as
              the import accepts, so admins can export → edit → re-import.
              Feb 2026 R284 · Switched to authenticated blob download. */}
          <Button variant="outline" size="sm" className="sm:size-default"
                   onClick={() => downloadXlsxAuth(
                     `/workspaces/${wsId}/vendors/export.xlsx`,
                     "vendors.xlsx")}
                   data-testid="vendor-export-xlsx-btn">
            <Download className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Export Excel</span><span className="sm:hidden">XLSX</span>
          </Button>
          <Button variant="outline" size="sm" className="sm:size-default" onClick={() => setImporting(true)} data-testid="vendor-import-btn">
            <Upload className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Bulk import</span><span className="sm:hidden">Import</span>
          </Button>
          <Button onClick={() => setEditing({})} data-testid="vendor-add-btn" size="sm" className="sm:size-default"><Plus className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">New vendor</span><span className="sm:hidden">New</span></Button>
        </div>
      </div>
      <Card><CardContent className="p-0">
        <AdvancedTable
          tableKey="finance-vendors"
          title="Vendors"
          rows={vendors}
          onRowClick={(v) => setEditing(v)}
          columns={[
            { key: "name", label: "Name", width: 200, sortable: true, filterType: "text", frozen: true },
            { key: "gstin", label: "GSTIN", width: 150, sortable: true, filterType: "text",
              render: (v) => <span className="font-mono text-xs">{v.gstin || "—"}</span> },
            { key: "pan", label: "PAN", width: 120, sortable: true, filterType: "text",
              render: (v) => <span className="font-mono text-xs">{v.pan || "—"}</span> },
            { key: "city", label: "City", width: 140, sortable: true, filterType: "text",
              render: (v) => <span className="text-xs">{v.city || "—"}</span> },
            { key: "state", label: "State", width: 120, sortable: true, filterType: "text",
              render: (v) => <span className="text-xs">{v.state ? `${v.state}${v.state_code ? ` (${v.state_code})` : ""}` : "—"}</span> },
            { key: "default_tds", label: "TDS default", width: 140,
              accessor: (v) => v.default_tds_section ? `${v.default_tds_section} @ ${v.default_tds_rate}%` : "",
              render: (v) => <span className="text-xs">{v.default_tds_section ? `${v.default_tds_section} @ ${v.default_tds_rate}%` : "—"}</span> },
            { key: "payment_terms_days", label: "Terms", width: 90, sortable: true, align: "right", type: "number",
              render: (v) => <span className="text-xs">{v.payment_terms_days || 0}d</span> },
            { key: "contact", label: "Contact", width: 200, filterType: "text",
              accessor: (v) => v.contact_email || v.contact_phone || "",
              render: (v) => <span className="text-xs">{v.contact_email || v.contact_phone || "—"}</span> },
            // Nov 2026 · Per-vendor last-12-month expense sparkline.
            // Hides on the smallest viewport (card-view handles it).
            { key: "spend_trend", label: "12-mo spend", width: 160,
              sortable: true, align: "right", type: "number",
              accessor: (v) => Number(spend.vendors?.[v.id]?.total || 0),
              render: (v) => {
                const s = spend.vendors?.[v.id];
                return (
                  <div className="flex items-center gap-2 justify-end" data-testid={`vendor-spark-${v.id}`}>
                    <Sparkline values={s?.series || []} />
                    <span className="font-mono text-[10px] text-muted-foreground w-16 text-right">
                      {s?.total ? fmt(s.total, "INR") : "—"}
                    </span>
                  </div>
                );
              } },
          ]}
          emptyTitle="No vendors yet"
          emptyDescription="Add a vendor to auto-apply GSTIN + TDS defaults when creating bills."
        />
      </CardContent></Card>
      {editing && (
        <VendorFormDialog
          wsId={wsId}
          vendor={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {importing && (
        <VendorImportDialog
          wsId={wsId}
          onClose={() => setImporting(false)}
          onDone={() => { setImporting(false); load(); }}
        />
      )}
    </div>
  );
}


function VendorFormDialog({ wsId, vendor, onClose, onSaved }) {
  const isNew = !vendor?.id;
  const empty = {
    name: "", legal_name: "", gstin: "", pan: "",
    default_tds_section: "", default_tds_rate: 0,
    payment_terms_days: 30, contact_email: "", contact_phone: "",
    address_line1: "", address_line2: "", city: "", state: "",
    state_code: "", pincode: "", country: "India",
    is_active: true,
  };
  const [form, setForm] = useState(() => ({ ...empty, ...(vendor || {}) }));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const composed = [form.address_line1, form.address_line2, form.city,
        form.state, form.pincode, form.country]
        .map((s) => (s || "").trim()).filter(Boolean).join(", ");
      const payload = {
        ...form,
        address: composed || undefined,
        default_tds_rate: Number(form.default_tds_rate) || 0,
        payment_terms_days: Number(form.payment_terms_days) || 0,
      };
      if (isNew) {
        await api.post(`/finance/workspaces/${wsId}/vendors`, payload);
        toast.success("Vendor added");
      } else {
        await api.patch(`/finance/vendors/${vendor.id}`, payload);
        toast.success("Vendor updated");
      }
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="vendor-create-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New vendor" : `Edit vendor · ${vendor.name}`}</DialogTitle>
          <DialogDescription>Vendor master with GSTIN + TDS defaults auto-applied on bill entry.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name-input" className="h-10" /></div>
            <div><Label>Legal name</Label><Input value={form.legal_name || ""} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="vendor-legal-input" className="h-10" /></div>
            <div><Label>GSTIN</Label><Input maxLength={15} value={form.gstin || ""} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} data-testid="vendor-gstin-input" className="h-10 font-mono" /></div>
            <div><Label>PAN</Label><Input maxLength={10} value={form.pan || ""} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} data-testid="vendor-pan-input" className="h-10 font-mono" /></div>
            <div><Label>Default TDS section</Label><Input value={form.default_tds_section || ""} onChange={(e) => setForm({ ...form, default_tds_section: e.target.value.toUpperCase() })} data-testid="vendor-tds-section-input" className="h-10" /></div>
            <div><Label>Default TDS rate %</Label><Input type="number" step="0.01" value={form.default_tds_rate || 0} onChange={(e) => setForm({ ...form, default_tds_rate: e.target.value })} data-testid="vendor-tds-rate-input" className="h-10" /></div>
            <div><Label>Payment terms (days)</Label><Input type="number" value={form.payment_terms_days || 0} onChange={(e) => setForm({ ...form, payment_terms_days: e.target.value })} data-testid="vendor-terms-input" className="h-10" /></div>
            <div><Label>Contact email</Label><Input type="email" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="vendor-email-input" className="h-10" /></div>
            <div><Label>Contact phone</Label><Input type="tel" value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="vendor-phone-input" className="h-10" /></div>
          </div>
          <div className="pt-2 border-t border-border">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Billing address</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Address line 1</Label><Input value={form.address_line1 || ""} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} data-testid="vendor-addr1-input" className="h-10" placeholder="Street, building, floor" /></div>
              <div className="sm:col-span-2"><Label>Address line 2</Label><Input value={form.address_line2 || ""} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} data-testid="vendor-addr2-input" className="h-10" placeholder="Landmark, area (optional)" /></div>
              <div><Label>City</Label><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="vendor-city-input" className="h-10" /></div>
              <div><Label>State</Label><Input value={form.state || ""} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="vendor-state-input" className="h-10" placeholder="Maharashtra" /></div>
              <div><Label>GST state code</Label><Input maxLength={4} value={form.state_code || ""} onChange={(e) => setForm({ ...form, state_code: e.target.value.replace(/[^0-9]/g, "") })} data-testid="vendor-state-code-input" className="h-10 font-mono" placeholder="27" /></div>
              <div><Label>Pincode</Label><Input maxLength={12} value={form.pincode || ""} onChange={(e) => setForm({ ...form, pincode: e.target.value })} data-testid="vendor-pincode-input" className="h-10 font-mono" placeholder="400001" /></div>
              <div className="sm:col-span-2"><Label>Country</Label><Input value={form.country || "India"} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="vendor-country-input" className="h-10" /></div>
            </div>
          </div>
        </div>
        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onClose} className="w-full sm:w-auto">Cancel</Button>
          <Button onClick={submit} disabled={!form.name || busy} data-testid="vendor-submit" className="w-full sm:w-auto">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : (isNew ? "Create vendor" : "Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function VendorImportDialog({ wsId, onClose, onDone }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parseCsv = (text) => {
    // Minimal RFC-4180-ish CSV parser (handles quoted cells + escaped
    // quotes). No third-party lib needed for a workspace-migration flow.
    const out = [];
    let field = "", record = [], inQ = false, i = 0;
    while (i < text.length) {
      const c = text[i];
      if (inQ) {
        if (c === '"' && text[i + 1] === '"') { field += '"'; i += 2; continue; }
        if (c === '"') { inQ = false; i += 1; continue; }
        field += c; i += 1; continue;
      }
      if (c === '"') { inQ = true; i += 1; continue; }
      if (c === ",") { record.push(field); field = ""; i += 1; continue; }
      if (c === "\r") { i += 1; continue; }
      if (c === "\n") { record.push(field); out.push(record); record = []; field = ""; i += 1; continue; }
      field += c; i += 1;
    }
    if (field.length || record.length) { record.push(field); out.push(record); }
    if (out.length === 0) return { headers: [], rows: [] };
    const hdrs = out[0].map((h) => (h || "").trim());
    const dataRows = out.slice(1)
      .filter((r) => r.some((c) => (c || "").trim() !== ""))
      .map((r) => Object.fromEntries(hdrs.map((h, i2) => [h, (r[i2] || "").trim()])));
    return { headers: hdrs, rows: dataRows };
  };

  const onFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      const { headers: h, rows: r } = parseCsv(text);
      setHeaders(h); setRows(r); setResult(null);
      if (r.length === 0) toast.error("No data rows detected in CSV");
    } catch (err) {
      toast.error("Could not read CSV file");
    }
    e.target.value = "";
  };

  const submit = async () => {
    if (rows.length === 0) { toast.error("Choose a CSV first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/finance/workspaces/${wsId}/vendors/bulk-import`, { rows });
      setResult(data);
      const parts = [];
      if (data.created) parts.push(`${data.created} added`);
      if (data.updated) parts.push(`${data.updated} updated`);
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      toast.success(`Import complete — ${parts.join(" · ")}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Import failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) (result ? onDone() : onClose()); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="vendor-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import vendors</DialogTitle>
          <DialogDescription>
            Upload a CSV of your existing vendor master. Recognised column headers
            (case-insensitive): <code className="text-[10px]">name</code>,{" "}
            <code className="text-[10px]">legal_name</code>,{" "}
            <code className="text-[10px]">gstin</code>,{" "}
            <code className="text-[10px]">pan</code>,{" "}
            <code className="text-[10px]">contact_email</code>,{" "}
            <code className="text-[10px]">contact_phone</code>,{" "}
            <code className="text-[10px]">address_line1</code>,{" "}
            <code className="text-[10px]">address_line2</code>,{" "}
            <code className="text-[10px]">city</code>,{" "}
            <code className="text-[10px]">state</code>,{" "}
            <code className="text-[10px]">state_code</code>,{" "}
            <code className="text-[10px]">pincode</code>,{" "}
            <code className="text-[10px]">country</code>,{" "}
            <code className="text-[10px]">default_tds_section</code>,{" "}
            <code className="text-[10px]">default_tds_rate</code>,{" "}
            <code className="text-[10px]">payment_terms_days</code>. Rows with the
            same GSTIN or exact name update existing vendors instead of duplicating.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={onFilePick} data-testid="vendor-import-file-input" />
          <div className="flex flex-col sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="w-full sm:w-auto" data-testid="vendor-import-choose">
              <Upload className="w-4 h-4 mr-2" />Choose CSV
            </Button>
            <a
              className="text-xs text-primary hover:underline self-center"
              href={`data:text/csv;charset=utf-8,${encodeURIComponent("name,legal_name,gstin,pan,contact_email,contact_phone,address_line1,address_line2,city,state,state_code,pincode,country,default_tds_section,default_tds_rate,payment_terms_days\nAcme Supplies,Acme Supplies Pvt Ltd,27ABCDE1234F1Z5,ABCDE1234F,billing@acme.io,+91-9999999999,742 Evergreen Terrace,Near Kwik-E-Mart,Mumbai,Maharashtra,27,400001,India,194J,10,30\n")}`}
              download="vendors-template.csv"
              data-testid="vendor-import-template"
            >
              Download starter template
            </a>
          </div>
          {rows.length > 0 && !result && (
            <div className="border border-border p-3 text-xs bg-muted/30" data-testid="vendor-import-preview">
              <div className="font-semibold mb-1">Preview · {rows.length} row{rows.length === 1 ? "" : "s"} ready</div>
              <div className="text-muted-foreground text-[11px] mb-2">Headers detected: {headers.join(", ") || "(none)"}</div>
              <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                {rows.slice(0, 8).map((r, i) => (
                  <li key={i} className="truncate"><strong>{r.name || r.Name || r["Vendor"] || "(missing name)"}</strong> · {r.gstin || r.GSTIN || "no gstin"}</li>
                ))}
                {rows.length > 8 && <li className="italic text-muted-foreground">…and {rows.length - 8} more</li>}
              </ul>
            </div>
          )}
          {result && (
            <div className="border border-border p-3 text-xs bg-emerald-50 dark:bg-emerald-950/20 space-y-1" data-testid="vendor-import-result">
              <div><strong>Created:</strong> {result.created}</div>
              <div><strong>Updated:</strong> {result.updated}</div>
              <div><strong>Skipped:</strong> {result.skipped}</div>
              {result.skipped > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer">Show skipped rows</summary>
                  <ul className="mt-1 space-y-0.5 text-[11px]">
                    {(result.results || []).filter((r) => r.status === "skipped").map((r, i) => (
                      <li key={i}>Row {r.row}: {r.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => (result ? onDone() : onClose())} className="w-full sm:w-auto">
            {result ? "Done" : "Cancel"}
          </Button>
          {!result && (
            <Button onClick={submit} disabled={rows.length === 0 || busy} className="w-full sm:w-auto" data-testid="vendor-import-submit">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing…</> : `Import ${rows.length} vendor${rows.length === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BillsTab({ wsId }) {
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [accts, setAccts] = useState([]);
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [selBill, setSelBill] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkPayOpen, setBulkPayOpen] = useState(false);
  // Feb 2026 · Which bill's attachments are shown in the lightbox.
  const [previewBill, setPreviewBill] = useState(null);
  // Feb 2026 R284 · Bulk-import bills dialog.
  const [billImportOpen, setBillImportOpen] = useState(false);
  // Feb 2026 R284 · Track template + export download state so the
  // buttons show a spinner + we can surface errors instead of the
  // previous silent-failure (window.location.href stripped the auth
  // token, backend returned 401 with no user feedback).
  const [dlBusy, setDlBusy] = useState(null); // 'template' | 'export' | null

  // Feb 2026 R284 · Wraps the shared `downloadXlsxAuth` helper with a
  // per-button spinner state (Template vs. Export).
  const downloadXlsx = async (path, filename, kind) => {
    setDlBusy(kind);
    await downloadXlsxAuth(path, filename);
    setDlBusy(null);
  };
  const empty = { vendor_id: "", bill_no: "", bill_date: new Date().toISOString().slice(0, 10), due_date: "", category_account_id: "", sub_account_id: "", amount: 0, currency: "INR", cgst_pct: 0, sgst_pct: 0, igst_pct: 0, tds_section: "", tds_rate: 0, hsn_sac_code: "", place_of_supply: "", client_id: "", project_id: "", is_client_reimbursable: false, reimbursement_status: "not_received", category_id: "", sub_category_id: "" };
  const [form, setForm] = useState(empty);
  // Nov 2026 · Dup-fix: lock the submit button on in-flight requests and
  // mint an idempotency token per dialog-open so a retry collapses into
  // the same bill. This is the fix for the prod duplicate-bills bug.
  const [submitting, setSubmitting] = useState(false);
  const clientTokenRef = useRef(null);
  // Nov 2026 · Duplicate inspector — grouped (vendor, date, amount)
  // tuples with >1 row. Drives the "possible duplicate" row badge
  // and the Merge dialog.
  const [dupGroups, setDupGroups] = useState([]);
  const [dupBillIds, setDupBillIds] = useState(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  // Nov 2026 · Bulk category / sub-category assignment.
  const [bulkCatOpen, setBulkCatOpen] = useState(false);
  const [bulkCatForm, setBulkCatForm] = useState({ category_account_id: "", sub_account_id: "" });
  // Nov 2026 · New expense_categories taxonomy — loaded once per tab mount.
  // `catName` is a flat `{id: name}` map for column renders;
  // `catOptions` + `subOptions` are the AdvancedTable filter dropdown
  // option arrays. Reload after Settings edits via load().
  const [taxonomy, setTaxonomy] = useState([]);
  const catName = useMemo(() => Object.fromEntries(taxonomy.map((c) => [c.id, c.name])), [taxonomy]);
  const catOptions = useMemo(() => taxonomy.filter((c) => !c.parent_id).map((c) => ({ value: c.id, label: c.name })), [taxonomy]);
  const subOptions = useMemo(() => taxonomy.filter((c) => c.parent_id).map((c) => ({ value: c.id, label: c.name })), [taxonomy]);
  // Nov 2026 · Clients list — used by the Bill dialog's new "Assign
  // client" field + the Create-invoice-from-bill action.
  const [clientsList, setClientsList] = useState([]);
  // Nov 2026 · Create-invoice-from-bill dialog state.
  const [convertBill, setConvertBill] = useState(null);
  const [convertForm, setConvertForm] = useState({ markup_amount: 0, markup_pct: 0, line_description: "", due_date: "", cgst_pct: 0, sgst_pct: 0, igst_pct: 0 });
  const [convertBusy, setConvertBusy] = useState(false);
  // Feb 2026 · Pre-uploaded attachments awaiting bill create. Shape:
  // `{file_id, filename, content_type, size, preview_url?}`.
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [payForm, setPayForm] = useState({ payment_date: new Date().toISOString().slice(0, 10), amount: 0, payment_account_id: "", method: "bank" });

  // Feb 2026 · Pay-now hint from AP dashboard — read once on mount,
  // clear so it doesn't reappear on refresh. Shows a banner + focus
  // filter so CFOs see exactly which bills to clear.
  const [payHint, setPayHint] = useState(() => {
    try {
      const raw = sessionStorage.getItem("ap_pay_now_hint");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Hints older than 60 s are stale — ignore them (probably a
      // stray tab reload rather than a fresh click).
      if (Date.now() - (parsed.ts || 0) > 60_000) return null;
      sessionStorage.removeItem("ap_pay_now_hint");
      return parsed;
    } catch (_e) { return null; }
  });

  const load = () => {
    api.get(`/finance/workspaces/${wsId}/bills`).then((r) => setBills(r.data.bills || []));
    // Nov 2026 · Fetch duplicate groups so the UI can flag suspect rows.
    api.get(`/finance/workspaces/${wsId}/bills/duplicates`).then((r) => {
      setDupGroups(r.data.groups || []);
      setDupBillIds(new Set(r.data.duplicate_bill_ids || []));
    }).catch(() => {});
    // Nov 2026 · Taxonomy + clients for the new columns + Bill dialog.
    api.get(`/finance/workspaces/${wsId}/expense-categories`).then((r) => setTaxonomy(r.data?.categories || [])).catch(() => {});
    clientsLookup(wsId).then((r) => setClientsList(r || [])).catch(() => {});
  };
  useEffect(() => {
    if (!wsId) return;
    load();
    api.get(`/finance/workspaces/${wsId}/vendors`).then((r) => setVendors(r.data.vendors || []));
    api.get(`/finance/workspaces/${wsId}/accounts`).then((r) => setAccts(r.data.accounts || []));
  }, [wsId]);

  const expAccts = accts.filter((a) => a.type === "expense");
  const payAccts = accts.filter((a) => ["bank", "cash"].includes(a.subtype));

  const openNewBill = () => {
    setForm(empty);
    setPendingAttachments([]);
    // Nov 2026 · Mint a fresh idempotency token per dialog session so
    // every rapid Save click within this dialog resolves to the same
    // bill. The token travels with the first request; the server
    // returns the existing bill on retries.
    clientTokenRef.current = (globalThis.crypto?.randomUUID?.() ||
                               `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setOpen(true);
  };

  const uploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    let firstOcrable = null;  // Feb 2026 · Run OCR against the FIRST
                                //  uploaded image / PDF to prefill fields.
    try {
      for (const file of fileList) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(
          `/workspaces/${wsId}/files/upload?scope=bill-attachments`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        const attachment = {
          file_id: data.id,
          filename: data.original_filename || file.name,
          content_type: data.content_type || file.type,
          size: data.size || file.size,
          download_url: data.download_url,
        };
        setPendingAttachments((prev) => [...prev, attachment]);
        if (!firstOcrable) {
          const ct = (attachment.content_type || "").toLowerCase();
          if (ct.startsWith("image/") || ct.includes("pdf")) {
            firstOcrable = attachment;
          }
        }
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
    // Fire-and-forget OCR — takes ~5-10 s. Non-blocking; user can keep
    // filling the form. Any successfully-extracted field is merged in
    // (empty fields are never overwritten).
    if (firstOcrable) {
      runOcr(firstOcrable);
    }
  };

  const [ocrBusy, setOcrBusy] = useState(false);
  const runOcr = async (attachment) => {
    setOcrBusy(true);
    try {
      const { data } = await api.post(
        `/finance/workspaces/${wsId}/bills/ocr-extract`,
        { file_id: attachment.file_id },
      );
      const f = data?.fields || {};
      // Merge only into empty fields so we never clobber the user's edits.
      setForm((prev) => {
        const merged = { ...prev };
        if (!prev.bill_no && f.bill_no) merged.bill_no = f.bill_no;
        if ((!prev.amount || Number(prev.amount) === 0) && f.amount) merged.amount = f.amount;
        if (!prev.bill_date && f.bill_date) merged.bill_date = f.bill_date;
        return merged;
      });
      // Try to auto-select vendor by GSTIN or fuzzy name.
      if (f.gstin || f.vendor_name) {
        const vGstin = (f.gstin || "").toUpperCase();
        const vName = (f.vendor_name || "").toLowerCase();
        const match = vendors.find((v) => (v.gstin || "").toUpperCase() === vGstin)
                      || (vName ? vendors.find((v) => (v.name || "").toLowerCase() === vName)
                                : null)
                      || (vName ? vendors.find((v) => (v.name || "").toLowerCase().includes(vName)
                                                       || vName.includes((v.name || "").toLowerCase()))
                                : null);
        if (match) {
          setForm((prev) => prev.vendor_id ? prev
            : { ...prev, vendor_id: match.id,
                tds_section: match.default_tds_section || prev.tds_section,
                tds_rate: match.default_tds_rate || prev.tds_rate });
          toast.success(`Auto-filled from photo · matched vendor ${match.name}`);
        } else {
          const parts = [];
          if (f.bill_no) parts.push(`bill # ${f.bill_no}`);
          if (f.amount) parts.push(`amount ${f.amount}`);
          if (f.gstin) parts.push(`GSTIN ${f.gstin}`);
          toast.success(`Auto-filled from photo${parts.length ? ` — ${parts.join(", ")}` : ""}`);
          if (f.gstin) {
            toast.message(`No existing vendor matched GSTIN ${f.gstin} — add them once and re-run.`);
          }
        }
      } else {
        toast.success("Auto-filled from photo");
      }
    } catch (e) {
      // OCR failures should never block bill entry — degrade quietly.
      const msg = apiErrorMessage(e, "OCR failed");
      // Only surface as a "note" so it isn't alarming when tesseract had a bad day.
      toast.message(`Couldn't auto-fill from photo · ${msg}`);
    } finally {
      setOcrBusy(false);
    }
  };

  const removePending = (fileId) => {
    setPendingAttachments((prev) => prev.filter((a) => a.file_id !== fileId));
  };

  const submit = async () => {
    if (submitting) return;  // Nov 2026 · Dup-fix: lock out rapid Save clicks.
    setSubmitting(true);
    try {
      const p = {
        ...form,
        amount: Number(form.amount),
        cgst_pct: Number(form.cgst_pct),
        sgst_pct: Number(form.sgst_pct),
        igst_pct: Number(form.igst_pct),
        tds_rate: Number(form.tds_rate),
        sub_account_id: form.sub_account_id || null,
        client_id: form.client_id || null,
        project_id: form.project_id || null,
        category_id: form.category_id || null,
        sub_category_id: form.sub_category_id || null,
        is_client_reimbursable: !!form.is_client_reimbursable,
        reimbursement_status: form.is_client_reimbursable ? (form.reimbursement_status || "not_received") : null,
        client_token: clientTokenRef.current,  // Nov 2026 · idempotency.
        attachments: pendingAttachments.map((a) => ({
          file_id: a.file_id, filename: a.filename,
          content_type: a.content_type, size: a.size,
        })),
      };
      await api.post(`/finance/workspaces/${wsId}/bills`, p);
      toast.success("Bill posted");
      setOpen(false);
      setForm(empty);
      setPendingAttachments([]);
      load();
    } catch (e) {
      // Nov 2026 · 409 means a bill with the same vendor + bill_no
      // already exists. Surface the server message so the PM can
      // check the existing row instead of silently retrying.
      const resp = e?.response?.data;
      if (e?.response?.status === 409 && resp?.detail?.code === "duplicate_bill_no") {
        toast.error(resp.detail.message || "Duplicate bill number for this vendor");
      } else {
        toast.error(apiErrorMessage(e, "Failed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Nov 2026 · Bulk assign category / sub-category to selected bills.
  const submitBulkCategory = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) { toast.error("Select bills first"); return; }
    if (!bulkCatForm.category_account_id && !bulkCatForm.sub_account_id) {
      toast.error("Choose a category or sub-category");
      return;
    }
    try {
      const { data } = await api.post(
        `/finance/workspaces/${wsId}/bills/bulk-update-category`,
        { bill_ids: ids,
          category_account_id: bulkCatForm.category_account_id || null,
          sub_account_id: bulkCatForm.sub_account_id || null });
      toast.success(`Updated ${data.modified_count} bill(s)`);
      setBulkCatOpen(false);
      setBulkCatForm({ category_account_id: "", sub_account_id: "" });
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk category update failed")); }
  };

  // Nov 2026 · Merge all duplicate groups — keeps earliest, soft-deletes rest.
  const [mergeBusy, setMergeBusy] = useState(false);
  const runMerge = async () => {
    if (!dupGroups.length) { toast.info("No duplicates detected"); return; }
    setMergeBusy(true);
    try {
      const { data } = await api.post(
        `/finance/workspaces/${wsId}/bills/merge-duplicates`,
        { groups: dupGroups, dry_run: false });
      toast.success(`Merged ${data.deleted_count} duplicate bill(s)`);
      setMergeOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Merge failed"));
    } finally { setMergeBusy(false); }
  };

  const submitPay = async () => {
    try {
      const p = { ...payForm, amount: Number(payForm.amount) };
      await api.post(`/finance/bills/${selBill.id}/record-payment`, p);
      toast.success("Payment recorded"); setPayOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const openPay = (b) => {
    setSelBill(b);
    setPayForm({ payment_date: new Date().toISOString().slice(0, 10), amount: b.outstanding, payment_account_id: "", method: "bank" });
    setPayOpen(true);
  };

  const vendorTds = (vid) => vendors.find((v) => v.id === vid);
  const onVendorChange = (vid) => {
    const v = vendorTds(vid);
    setForm({ ...form, vendor_id: vid, tds_section: v?.default_tds_section || form.tds_section, tds_rate: v?.default_tds_rate || form.tds_rate });
  };

  const outstandingBillIds = useMemo(() => {
    const ids = Array.from(selectedIds);
    return ids.filter((id) => {
      const b = bills.find((x) => x.id === id);
      return b && b.status !== "paid" && Number(b.outstanding || 0) > 0.01;
    });
  }, [selectedIds, bills]);

  const bulkMarkPaid = async (paymentAccountId, method) => {
    if (!outstandingBillIds.length) { toast.error("No outstanding bills selected"); return; }
    try {
      const { data } = await api.post(`/finance/workspaces/${wsId}/bills/bulk-mark-paid`, {
        bill_ids: outstandingBillIds,
        payment_date: new Date().toISOString().slice(0, 10),
        payment_account_id: paymentAccountId,
        method,
      });
      toast.success(`Paid ${data.paid} bill(s) · ${data.total_amount.toLocaleString()} INR${data.skipped ? ` · skipped ${data.skipped}` : ""}`);
      setBulkPayOpen(false);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk mark-paid failed")); }
  };

  // Nov 2026 · Confirm-proposed-bill dialog state. When PM clicks
  // "Confirm" on a `proposed` row, pre-fill with the recurring
  // template's estimate so they only need to tweak the actual amount.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBill, setConfirmBill] = useState(null);
  const [confirmForm, setConfirmForm] = useState({ bill_no: "", bill_date: "", due_date: "", amount: 0, notes: "" });
  const [confirmBusy, setConfirmBusy] = useState(false);
  const openConfirmProposed = (b) => {
    setConfirmBill(b);
    setConfirmForm({
      bill_no: "",
      bill_date: b.bill_date || new Date().toISOString().slice(0, 10),
      due_date: "",
      amount: Number(b.amount || 0),
      notes: b.notes || "",
    });
    setConfirmOpen(true);
  };
  const submitConfirmProposed = async () => {
    if (!confirmBill || confirmBusy) return;
    if (!confirmForm.bill_no || !confirmForm.bill_date || Number(confirmForm.amount) <= 0) {
      toast.error("Bill number, date and amount are required");
      return;
    }
    setConfirmBusy(true);
    try {
      await api.post(
        `/finance/workspaces/${wsId}/bills/${confirmBill.id}/confirm-proposed`,
        { bill_no: confirmForm.bill_no, bill_date: confirmForm.bill_date,
          due_date: confirmForm.due_date || null,
          amount: Number(confirmForm.amount),
          notes: confirmForm.notes || null });
      toast.success("Bill confirmed and journal posted");
      setConfirmOpen(false);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to confirm")); }
    finally { setConfirmBusy(false); }
  };

  // Nov 2026 · Open the convert-to-invoice dialog for a reimbursable bill.
  const openConvertDialog = (b) => {
    setConvertBill(b);
    setConvertForm({
      markup_amount: 0, markup_pct: 0,
      line_description: `Reimbursement — ${b.vendor_name || "vendor"} bill ${b.bill_no}`,
      due_date: "", cgst_pct: 0, sgst_pct: 0, igst_pct: 0,
    });
  };
  const submitConvert = async () => {
    if (!convertBill || convertBusy) return;
    setConvertBusy(true);
    try {
      const payload = {
        markup_amount: Number(convertForm.markup_amount) || 0,
        markup_pct: Number(convertForm.markup_pct) || 0,
        line_description: convertForm.line_description || null,
        due_date: convertForm.due_date || null,
        cgst_pct: Number(convertForm.cgst_pct) || 0,
        sgst_pct: Number(convertForm.sgst_pct) || 0,
        igst_pct: Number(convertForm.igst_pct) || 0,
      };
      const { data } = await api.post(
        `/finance/workspaces/${wsId}/bills/${convertBill.id}/convert-to-invoice`, payload);
      toast.success(`Invoice ${data.invoice?.doc_no} created — markup ${fmt(data.markup_amount, "INR")}`);
      setConvertBill(null);
      load();
    } catch (e) {
      const d = e?.response?.data;
      if (d?.detail?.code === "already_converted") toast.error(d.detail.message);
      else if (d?.detail?.code === "no_client") toast.error("Assign a client to this bill first (edit bill → Client).");
      else toast.error(apiErrorMessage(e, "Convert failed"));
    } finally { setConvertBusy(false); }
  };

  const selectedSum = useMemo(() => {
    let out = 0;
    selectedIds.forEach((id) => {
      const b = bills.find((x) => x.id === id);
      if (b && b.status !== "paid") out += Number(b.outstanding || 0);
    });
    return out;
  }, [selectedIds, bills]);

  return (
    <div className="space-y-4">
      {/* Feb 2026 · Pay-now banner — CFO clicked a top-overdue vendor
          on the AP dashboard. Highlights which vendor + amount + all
          their overdue bills. Dismissible; also cleared by re-navigating
          to /bills without the flag. */}
      {payHint && (
        <div className="border border-amber-400 bg-amber-50 text-amber-900 p-3 flex items-center gap-3"
              data-testid="pay-now-banner">
          <IndianRupee className="w-4 h-4 shrink-0" />
          <div className="flex-1 text-sm">
            <b>{payHint.vendor_name}</b> has <b>₹{Number(payHint.amount).toLocaleString("en-IN")}</b> overdue.
            Filter the list below and record a payment against each open bill.
          </div>
          <Button size="sm" variant="ghost"
                   onClick={() => setPayHint(null)}
                   className="rounded-none h-7 text-xs"
                   data-testid="pay-now-banner-dismiss">
            Dismiss
          </Button>
        </div>
      )}
      {/* Nov 2026 · Duplicate detector banner — shows aggregate count +
          Merge CTA. Hides itself when no groups remain. */}
      {dupGroups.length > 0 && (
        <div className="border border-amber-400 bg-amber-50 text-amber-900 p-3 flex items-center gap-3 flex-wrap"
              data-testid="bills-dup-banner">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <div className="flex-1 text-sm">
            <b>{dupGroups.length}</b> possible duplicate bill group{dupGroups.length === 1 ? "" : "s"} detected
            (<b>{dupBillIds.size}</b> bill{dupBillIds.size === 1 ? "" : "s"}, same vendor + date + amount).
          </div>
          <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                   onClick={() => setMergeOpen(true)}
                   data-testid="bills-dup-review">
            Review &amp; merge
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm text-muted-foreground">{bills.length} bills</div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Feb 2026 · Excel round-trip for Bills. Template download
              embeds vendor + GST-state + TDS-section dropdowns so
              accounts type dozens of bills without vendor-name typos.
              Feb 2026 R284 · Switched from window.location.href to an
              authenticated axios blob download — the previous version
              was silently 401'ing because bearer tokens live in
              localStorage, not a cookie. */}
          <Button variant="outline" size="sm" className="sm:size-default"
                   onClick={() => downloadXlsx(
                     `/workspaces/${wsId}/bills/template.xlsx`,
                     "bills-template.xlsx", "template")}
                   disabled={dlBusy === "template"}
                   data-testid="bill-template-btn">
            {dlBusy === "template"
              ? <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
              : <Download className="w-4 h-4 mr-1 sm:mr-2" />}
            <span className="hidden sm:inline">Template</span><span className="sm:hidden">Tpl</span>
          </Button>
          <Button variant="outline" size="sm" className="sm:size-default"
                   onClick={() => downloadXlsx(
                     `/workspaces/${wsId}/bills/export.xlsx`,
                     "bills.xlsx", "export")}
                   disabled={dlBusy === "export"}
                   data-testid="bill-export-xlsx-btn">
            {dlBusy === "export"
              ? <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
              : <Download className="w-4 h-4 mr-1 sm:mr-2" />}
            <span className="hidden sm:inline">Export Excel</span><span className="sm:hidden">XLSX</span>
          </Button>
          {/* R289h · Feb 2026 — Round-trip export (13-col template layout) */}
          <Button variant="outline" size="sm" className="sm:size-default"
                   onClick={() => downloadXlsx(
                     `/workspaces/${wsId}/bills/export.xlsx?template=true`,
                     "bills-roundtrip.xlsx", "roundtrip")}
                   disabled={dlBusy === "roundtrip"}
                   title="Same layout as the import template — edit and re-upload"
                   data-testid="bill-export-roundtrip-btn">
            {dlBusy === "roundtrip"
              ? <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
              : <Download className="w-4 h-4 mr-1 sm:mr-2" />}
            <span className="hidden sm:inline">Round-trip</span><span className="sm:hidden">RT</span>
          </Button>
          {/* Feb 2026 R284 · Bulk-import bills (preview + commit). */}
          <Button variant="outline" size="sm" className="sm:size-default"
                   onClick={() => setBillImportOpen(true)}
                   data-testid="bill-import-btn">
            <Upload className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Bulk import</span><span className="sm:hidden">Import</span>
          </Button>
          <Button onClick={openNewBill} data-testid="bill-add-btn" size="sm" className="sm:size-default"><Plus className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">New bill</span><span className="sm:hidden">New</span></Button>
        </div>
      </div>
      <Card><CardContent className="p-0">
        <AdvancedTable
          tableKey="bills"
          title="Bills"
          rows={bills}
          columns={[
            { key: "bill_date", label: "Date", width: 110, sortable: true, filterType: "date-range", frozen: true },
            { key: "bill_no", label: "Bill #", width: 140, sortable: true, filterType: "text",
              render: (b) => <span className="font-mono text-xs">{b.bill_no}</span> },
            { key: "vendor_name", label: "Vendor", width: 220, sortable: true, groupable: true, filterType: "text" },
            // Nov 2026 · Category + Sub-category columns driven by the
            // new expense_categories taxonomy. Both groupable so PMs can
            // slice the sheet by category inline.
            { key: "category_id", label: "Category", width: 140, sortable: true, groupable: true, filterType: "select",
              filterOptions: catOptions,
              accessor: (b) => catName[b.category_id] || "",
              render: (b) => <span className="text-xs">{catName[b.category_id] || <span className="text-muted-foreground">—</span>}</span> },
            { key: "sub_category_id", label: "Sub-category", width: 140, sortable: true, groupable: true, filterType: "select",
              pinAfter: "category_id",
              filterOptions: subOptions,
              accessor: (b) => catName[b.sub_category_id] || "",
              render: (b) => <span className="text-xs">{catName[b.sub_category_id] || <span className="text-muted-foreground">—</span>}</span> },
            // Nov 2026 · Reimbursable + reimbursement status columns.
            { key: "is_client_reimbursable", label: "Reimb.", width: 70, sortable: true, groupable: true, align: "center",
              filterType: "select",
              filterOptions: [{ value: "true", label: "Yes" }, { value: "false", label: "No" }],
              accessor: (b) => b.is_client_reimbursable ? "true" : "false",
              render: (b) => b.is_client_reimbursable
                ? <Badge variant="outline" className="h-5 text-[9px] bg-emerald-50 text-emerald-700 border-emerald-300">Yes</Badge>
                : <span className="text-xs text-muted-foreground">—</span> },
            { key: "reimbursement_status", label: "Reimb. status", width: 140, sortable: true, groupable: true,
              filterType: "select",
              filterOptions: Object.entries(REIMB_STATUS_META).map(([v, m]) => ({ value: v, label: m.label })),
              render: (b) => {
                if (!b.is_client_reimbursable) return <span className="text-xs text-muted-foreground">—</span>;
                const m = REIMB_STATUS_META[b.reimbursement_status || "not_received"];
                return <Badge className={`h-5 text-[10px] font-normal ${m?.color || "bg-slate-100"}`}>{m?.label || b.reimbursement_status}</Badge>;
              } },
            { key: "ap_balance", label: "Amount", width: 140, sortable: true, align: "right", type: "currency", sum: true, filterType: "number-range",
              accessor: (b) => Number(b.ap_balance || 0),
              render: (b) => <span className="font-mono">{fmt(b.ap_balance, b.currency)}</span> },
            { key: "outstanding", label: "Outstanding", width: 140, sortable: true, align: "right", type: "currency", sum: true,
              accessor: (b) => Number(b.outstanding || 0),
              render: (b) => <span className="font-mono">{fmt(b.outstanding, b.currency)}</span> },
            // Feb 2026 · Attachment count + click-to-view.
            { key: "attachments", label: "Files", width: 80, align: "center",
              accessor: (b) => (b.attachments || []).length,
              render: (b) => {
                const count = (b.attachments || []).length;
                if (!count) return <span className="text-xs text-muted-foreground">—</span>;
                return (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setPreviewBill(b); }}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:bg-primary/10 px-1.5 py-0.5 rounded"
                    aria-label="Preview attachments"
                    data-testid={`bill-attachments-${b.id}`}
                  >
                    <Paperclip className="w-3 h-3" />{count}
                  </button>
                );
              } },
            { key: "status", label: "Status", width: 110, sortable: true, groupable: true, filterType: "select",
              filterOptions: [
                { value: "proposed", label: "proposed" }, { value: "draft", label: "draft" },
                { value: "open", label: "open" },
                { value: "partial", label: "partial" }, { value: "paid", label: "paid" },
              ],
              render: (b) => (
                <Badge
                  variant={b.status === "paid" ? "default" : b.status === "partial" ? "outline" : "secondary"}
                  className={b.status === "proposed" ? "opacity-70 italic border-dashed" : ""}
                  data-testid={`bill-status-${b.id}`}>
                  {b.status}
                </Badge>
              ) },
          ]}
          emptyTitle="No bills yet"
          emptyDescription="GST + TDS auto-computed. Auto-posts a balanced journal on save."
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          renderBulkBar={() => (
            <>
              <span className="text-[11px] text-muted-foreground font-mono" data-testid="bills-bulk-outstanding-total">
                Outstanding: {fmt(selectedSum, "INR")}
              </span>
              <Button size="sm" variant="outline" className="rounded-none h-7"
                       onClick={() => setBulkCatOpen(true)}
                       data-testid="bills-bulk-category">
                <Tag className="w-3.5 h-3.5 mr-1" />Set category
              </Button>
              <Button size="sm" className="rounded-none h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setBulkPayOpen(true)} data-testid="bills-bulk-pay">
                Mark {outstandingBillIds.length} paid
              </Button>
            </>
          )}
          renderRowActions={(b) => (
            <div className="flex items-center gap-1">
              {dupBillIds.has(b.id) && (
                <Badge variant="outline" className="h-6 border-amber-400 text-amber-700 text-[10px] px-1.5"
                        data-testid={`bill-dup-badge-${b.id}`}
                        title="Same vendor + date + amount as another bill">
                  <AlertTriangle className="w-3 h-3 mr-0.5" />Possible duplicate
                </Badge>
              )}
              {b.status === "proposed" && (
                <Button variant="outline" size="sm"
                         onClick={(e) => { e.stopPropagation(); openConfirmProposed(b); }}
                         className="h-7 text-xs border-blue-400 text-blue-700"
                         data-testid={`bill-confirm-${b.id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Confirm
                </Button>
              )}
              {b.status !== "paid" && b.status !== "proposed" && (
                <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); openPay(b); }} className="h-7 text-xs" data-testid={`bill-pay-${b.id}`}>Pay</Button>
              )}
              {/* Nov 2026 · Convert reimbursable bill → sales invoice */}
              {b.is_client_reimbursable && !b.invoice_id && (
                <Button variant="outline" size="sm"
                         onClick={(e) => { e.stopPropagation(); openConvertDialog(b); }}
                         className="h-7 text-xs border-blue-400 text-blue-700"
                         data-testid={`bill-convert-${b.id}`}
                         title="Create a sales invoice for the linked client">
                  <Receipt className="w-3 h-3 mr-0.5" />Invoice
                </Button>
              )}
              {b.invoice_id && (
                <Badge variant="outline" className="h-6 border-blue-300 text-blue-700 text-[10px]" title="Linked invoice">
                  <Receipt className="w-3 h-3 mr-0.5" />Invoiced
                </Badge>
              )}
            </div>
          )}
        />
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bill-create-dialog">
          <DialogHeader><DialogTitle>New bill</DialogTitle><DialogDescription>GST + TDS auto-computed. Auto-posts a balanced journal on save.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Vendor *</Label>
                <Select value={form.vendor_id} onValueChange={onVendorChange}>
                  <SelectTrigger data-testid="bill-vendor-select" className="h-10"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Bill # *</Label><Input value={form.bill_no} onChange={(e) => setForm({ ...form, bill_no: e.target.value })} data-testid="bill-no-input" className="h-10 font-mono" /></div>
              <div><Label>Expense account *</Label>
                <Select value={form.category_account_id} onValueChange={(v) => setForm({ ...form, category_account_id: v, sub_account_id: "" })}>
                  <SelectTrigger data-testid="bill-account-select" className="h-10"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{expAccts.filter((a) => !a.parent_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Sub-category (optional)</Label>
                <Select value={form.sub_account_id} onValueChange={(v) => setForm({ ...form, sub_account_id: v })}>
                  <SelectTrigger data-testid="bill-sub-account-select" className="h-10"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>{expAccts.filter((a) => a.parent_id === form.category_account_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Bill date *</Label><Input type="date" value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value })} data-testid="bill-date-input" className="h-10" /></div>
              <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="bill-due-input" className="h-10" /></div>
              <div><Label>Amount (net) *</Label><Input inputMode="decimal" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="bill-amount-input" className="h-10 font-mono" /></div>
              <div><Label>CGST %</Label><Input inputMode="decimal" type="number" step="0.01" value={form.cgst_pct} onChange={(e) => setForm({ ...form, cgst_pct: e.target.value })} data-testid="bill-cgst-input" className="h-10 font-mono" /></div>
              <div><Label>SGST %</Label><Input inputMode="decimal" type="number" step="0.01" value={form.sgst_pct} onChange={(e) => setForm({ ...form, sgst_pct: e.target.value })} data-testid="bill-sgst-input" className="h-10 font-mono" /></div>
              <div><Label>IGST %</Label><Input inputMode="decimal" type="number" step="0.01" value={form.igst_pct} onChange={(e) => setForm({ ...form, igst_pct: e.target.value })} data-testid="bill-igst-input" className="h-10 font-mono" /></div>
              <div><Label>TDS section</Label><Input value={form.tds_section} onChange={(e) => setForm({ ...form, tds_section: e.target.value.toUpperCase() })} data-testid="bill-tds-section-input" className="h-10 font-mono" /></div>
              <div><Label>TDS rate %</Label><Input inputMode="decimal" type="number" step="0.01" value={form.tds_rate} onChange={(e) => setForm({ ...form, tds_rate: e.target.value })} data-testid="bill-tds-rate-input" className="h-10 font-mono" /></div>
              {/* Nov 2026 · Taxonomy (separate from the GL expense a/c) */}
              <div><Label>Category</Label>
                <Select value={form.category_id || ""} onValueChange={(v) => setForm({ ...form, category_id: v, sub_category_id: "" })}>
                  <SelectTrigger data-testid="bill-category-select" className="h-10"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>{taxonomy.filter((c) => !c.parent_id && c.is_active !== false).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Sub-category</Label>
                <Select value={form.sub_category_id || ""} onValueChange={(v) => setForm({ ...form, sub_category_id: v })}>
                  <SelectTrigger data-testid="bill-subcategory-select" className="h-10"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>{taxonomy.filter((c) => c.parent_id === form.category_id && c.is_active !== false).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {/* Nov 2026 · Client-reimbursable fields */}
              <div><Label>Assign to client (optional)</Label>
                <Select value={form.client_id || ""} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="bill-client-select" className="h-10"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>{clientsList.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex flex-col justify-end gap-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.is_client_reimbursable} onChange={(e) => setForm({ ...form, is_client_reimbursable: e.target.checked })} data-testid="bill-reimb-check" />
                  <span className="text-xs">Client-reimbursable</span>
                </label>
                {form.is_client_reimbursable && (
                  <Select value={form.reimbursement_status || "not_received"} onValueChange={(v) => setForm({ ...form, reimbursement_status: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="bill-reimb-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(REIMB_STATUS_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
            </div>
            {/* Feb 2026 · Attachments (files or camera capture) */}
            <div className="pt-2 border-t border-border">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Attachments</div>
                  <div className="text-xs text-muted-foreground">Attach the scanned bill / PDF / photo. Multiple files supported.</div>
                </div>
              </div>
              <BillAttachmentsPicker
                pending={pendingAttachments}
                uploading={uploading}
                ocrBusy={ocrBusy}
                onUpload={uploadFiles}
                onRemove={removePending}
              />
            </div>
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setOpen(false)} className="w-full sm:w-auto">Cancel</Button>
            <Button onClick={submit} disabled={!form.vendor_id || !form.bill_no || !form.category_account_id || !form.amount || uploading || submitting} data-testid="bill-submit" className="w-full sm:w-auto">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Posting…</> : uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading…</> : "Post bill"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="bill-pay-dialog">
          <DialogHeader><DialogTitle>Record payment</DialogTitle><DialogDescription>Bill {selBill?.bill_no} — outstanding {fmt(selBill?.outstanding, selBill?.currency)}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Payment date</Label><Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} data-testid="pay-date-input" /></div>
            <div><Label>Amount</Label><Input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="pay-amount-input" /></div>
            <div><Label>Payment account</Label>
              <Select value={payForm.payment_account_id} onValueChange={(v) => setPayForm({ ...payForm, payment_account_id: v })}>
                <SelectTrigger data-testid="pay-account-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{payAccts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Method</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["bank", "cash", "upi", "cheque", "card", "other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={submitPay} disabled={!payForm.payment_account_id || !payForm.amount} data-testid="pay-submit">Record</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkPayDialog
        open={bulkPayOpen}
        onOpenChange={setBulkPayOpen}
        count={outstandingBillIds.length}
        totalOutstanding={selectedSum}
        payAccts={payAccts}
        onConfirm={bulkMarkPaid}
      />
      {previewBill && (
        <BillAttachmentPreview
          bill={previewBill}
          wsId={wsId}
          onClose={() => setPreviewBill(null)}
          onChanged={() => { load(); }}
        />
      )}
      {billImportOpen && (
        <BillImportDialog
          wsId={wsId}
          onClose={() => setBillImportOpen(false)}
          onDone={() => { setBillImportOpen(false); load(); }}
        />
      )}

      {/* Nov 2026 · Bulk category / sub-category update dialog */}
      <Dialog open={bulkCatOpen} onOpenChange={setBulkCatOpen}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="bills-bulk-category-dialog">
          <DialogHeader>
            <DialogTitle>Set category on {selectedIds.size} bill{selectedIds.size === 1 ? "" : "s"}</DialogTitle>
            <DialogDescription>Patches the bill records only. The posted journal entries are not re-booked — if you need to change the GL account for an already-posted bill, reverse + re-post it individually.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Category (expense account)</Label>
              <Select value={bulkCatForm.category_account_id} onValueChange={(v) => setBulkCatForm({ ...bulkCatForm, category_account_id: v })}>
                <SelectTrigger data-testid="bulk-cat-select" className="h-10"><SelectValue placeholder="Keep existing…" /></SelectTrigger>
                <SelectContent>{expAccts.filter((a) => !a.parent_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sub-category (optional)</Label>
              <Select value={bulkCatForm.sub_account_id} onValueChange={(v) => setBulkCatForm({ ...bulkCatForm, sub_account_id: v })}>
                <SelectTrigger data-testid="bulk-sub-select" className="h-10"><SelectValue placeholder="Keep existing…" /></SelectTrigger>
                <SelectContent>{expAccts.filter((a) => a.parent_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkCatOpen(false)}>Cancel</Button>
            <Button onClick={submitBulkCategory} disabled={!bulkCatForm.category_account_id && !bulkCatForm.sub_account_id} data-testid="bulk-cat-submit">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nov 2026 · Duplicate inspector dialog */}
      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bills-merge-dialog">
          <DialogHeader>
            <DialogTitle>Review possible duplicates</DialogTitle>
            <DialogDescription>Each group below has multiple bills with the same vendor, date, and amount. Merging keeps the oldest row and soft-deletes the rest (their journal entries are reversed, so your GL stays balanced).</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-xs">
            {dupGroups.length === 0 && <div className="text-sm text-muted-foreground">No duplicates found.</div>}
            {dupGroups.map((g, i) => (
              <div key={i} className="border border-amber-300 bg-amber-50/40 p-2" data-testid={`dup-group-${i}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div><b>{g.vendor_name}</b> · {g.bill_date} · {fmt(g.amount, "INR")}</div>
                  <Badge variant="outline" className="text-[10px]">{g.count} rows</Badge>
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 break-all">
                  {g.bill_nos.join(" · ")}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMergeOpen(false)}>Cancel</Button>
            <Button onClick={runMerge} disabled={!dupGroups.length || mergeBusy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="merge-run">
              {mergeBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Merging…</> : <><Trash2 className="w-4 h-4 mr-2" />Keep oldest · delete rest</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nov 2026 · Confirm-proposed dialog — flips status proposed → open */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="bill-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm proposed bill</DialogTitle>
            <DialogDescription>
              {confirmBill && (<>
                <b>{confirmBill.vendor_name}</b> · estimate {fmt(confirmBill.amount, confirmBill.currency)}. Edit the actuals before confirming — this will book the GL journal.
              </>)}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Bill # *</Label><Input value={confirmForm.bill_no} onChange={(e) => setConfirmForm({ ...confirmForm, bill_no: e.target.value })} data-testid="confirm-bill-no" className="h-10 font-mono" /></div>
            <div><Label>Bill date *</Label><Input type="date" value={confirmForm.bill_date} onChange={(e) => setConfirmForm({ ...confirmForm, bill_date: e.target.value })} data-testid="confirm-bill-date" className="h-10" /></div>
            <div><Label>Due date</Label><Input type="date" value={confirmForm.due_date} onChange={(e) => setConfirmForm({ ...confirmForm, due_date: e.target.value })} data-testid="confirm-bill-due" className="h-10" /></div>
            <div><Label>Actual amount *</Label><Input inputMode="decimal" type="number" step="0.01" value={confirmForm.amount} onChange={(e) => setConfirmForm({ ...confirmForm, amount: e.target.value })} data-testid="confirm-bill-amount" className="h-10 font-mono" /></div>
            <div className="sm:col-span-2"><Label>Notes</Label><Input value={confirmForm.notes} onChange={(e) => setConfirmForm({ ...confirmForm, notes: e.target.value })} data-testid="confirm-bill-notes" className="h-10" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={submitConfirmProposed} disabled={confirmBusy} data-testid="confirm-bill-submit" className="bg-blue-600 hover:bg-blue-700 text-white">
              {confirmBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Confirming…</> : <><CheckCircle2 className="w-4 h-4 mr-2" />Confirm &amp; post</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nov 2026 · Convert reimbursable bill → sales invoice */}
      <Dialog open={!!convertBill} onOpenChange={(v) => !v && setConvertBill(null)}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="bill-convert-dialog">
          <DialogHeader>
            <DialogTitle>Create sales invoice from bill</DialogTitle>
            <DialogDescription>
              {convertBill && (<>
                <b>{convertBill.vendor_name}</b> · {fmt(convertBill.amount, convertBill.currency)} · bill <code>{convertBill.bill_no}</code>.
                Invoice goes to <b>{clientsList.find((c) => c.id === convertBill.client_id)?.name || <span className="text-destructive">— no client assigned</span>}</b>.
              </>)}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Markup amount (flat)</Label><Input inputMode="decimal" type="number" step="0.01" value={convertForm.markup_amount} onChange={(e) => setConvertForm((s) => ({ ...s, markup_amount: e.target.value }))} data-testid="convert-markup-amt" className="h-10 font-mono" /></div>
            <div><Label>Markup %</Label><Input inputMode="decimal" type="number" step="0.01" value={convertForm.markup_pct} onChange={(e) => setConvertForm((s) => ({ ...s, markup_pct: e.target.value }))} data-testid="convert-markup-pct" className="h-10 font-mono" /></div>
            <div className="sm:col-span-2"><Label>Line description</Label><Input value={convertForm.line_description} onChange={(e) => setConvertForm((s) => ({ ...s, line_description: e.target.value }))} data-testid="convert-line-desc" className="h-10" /></div>
            <div><Label>Due date</Label><Input type="date" value={convertForm.due_date} onChange={(e) => setConvertForm((s) => ({ ...s, due_date: e.target.value }))} className="h-10" /></div>
            <div><Label>CGST %</Label><Input inputMode="decimal" type="number" step="0.01" value={convertForm.cgst_pct} onChange={(e) => setConvertForm((s) => ({ ...s, cgst_pct: e.target.value }))} className="h-10 font-mono" /></div>
            <div><Label>SGST %</Label><Input inputMode="decimal" type="number" step="0.01" value={convertForm.sgst_pct} onChange={(e) => setConvertForm((s) => ({ ...s, sgst_pct: e.target.value }))} className="h-10 font-mono" /></div>
            <div><Label>IGST %</Label><Input inputMode="decimal" type="number" step="0.01" value={convertForm.igst_pct} onChange={(e) => setConvertForm((s) => ({ ...s, igst_pct: e.target.value }))} className="h-10 font-mono" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConvertBill(null)}>Cancel</Button>
            <Button onClick={submitConvert} disabled={convertBusy || !convertBill?.client_id} data-testid="convert-submit" className="bg-blue-600 hover:bg-blue-700 text-white">
              {convertBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating…</> : <><Receipt className="w-4 h-4 mr-2" />Create invoice</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
// returns a preview plan (create / duplicate / error per row) → user
// confirms → server commits into `bills` collection.
function BillImportDialog({ wsId, onClose, onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);   // { token, stats, plan, missing_vendors, vendor_matches, ... }
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [skipErrors, setSkipErrors] = useState(true);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [result, setResult] = useState(null);     // commit result
  // Feb 2026 R285 · Inline vendor auto-create state.
  const [creatingVendors, setCreatingVendors] = useState(false);
  // R289m · Feb 2026 — Per-row vendor auto-link decisions.
  // raw_name → { action: 'link'|'create', vendor_id?, new_name?, gstin? }
  const [linkMap, setLinkMap] = useState({});

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setPreview(null); setResult(null); }
    e.target.value = "";
  };

  const runPreview = async (currentFile) => {
    const f = currentFile || file;
    if (!f) { toast.error("Choose an Excel file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(
        `/workspaces/${wsId}/bills/import/preview`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(data);
      const s = data.stats || {};
      const missing = (data.missing_vendors || []).length;
      toast.success(`Previewed ${s.total || 0} row(s) · ${s.create || 0} new · ${s.duplicate || 0} dup · ${s.error || 0} error${missing ? ` · ${missing} missing vendor(s)` : ""}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
    finally { setBusy(false); }
  };

  // Feb 2026 R285 · Inline vendor auto-create. Sends the distinct
  // unknown-vendor names from the preview to the bulk-create endpoint,
  // then re-runs the preview against the same file so unknown-vendor
  // errors flip to "new" rows. Kills the top import friction point.
  const createMissingVendors = async () => {
    const names = preview?.missing_vendors || [];
    if (!names.length) return;
    setCreatingVendors(true);
    try {
      const { data } = await api.post(
        `/finance/workspaces/${wsId}/vendors/bulk-create-from-names`,
        { names });
      toast.success(`Created ${data.count || 0} vendor(s) — re-scanning import…`);
      // Re-run preview against the same file so the new vendors resolve.
      await runPreview(file);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not create vendors")); }
    finally { setCreatingVendors(false); }
  };

  const commit = async () => {
    if (!preview?.token) return;
    setCommitting(true);
    try {
      const cleanMap = {};
      Object.entries(linkMap).forEach(([k, v]) => {
        if (v && v.action && v.action !== "auto") cleanMap[k] = v;
      });
      const { data } = await api.post(
        `/workspaces/${wsId}/purchases/import/commit`,
        { token: preview.token, skip_errors: skipErrors,
          skip_duplicates: skipDuplicates,
          vendor_link_map: Object.keys(cleanMap).length ? cleanMap : undefined });
      setResult(data);
      const s = data.stats || {};
      toast.success(`Imported — ${s.created || 0} new bill(s) · ${s.skipped || 0} skipped`);
    } catch (e) { toast.error(apiErrorMessage(e, "Import failed")); }
    finally { setCommitting(false); }
  };

  const downloadTemplate = () =>
    downloadXlsxAuth(`/workspaces/${wsId}/bills/template.xlsx`, "bills-template.xlsx");

  const stats = preview?.stats || {};
  const missingVendors = preview?.missing_vendors || [];

  return (
    <Dialog open onOpenChange={(v) => { if (!v) (result ? onDone() : onClose()); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bill-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import vendor bills</DialogTitle>
          <DialogDescription>
            Upload the filled Excel template. The template embeds vendor,
            GST-state and TDS-section dropdowns so bill data stays clean.
            You'll see a preview (created / duplicates / errors) before
            anything is committed. Duplicates match on <b>vendor + bill&nbsp;#</b>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden
                 onChange={onFilePick} data-testid="bill-import-file-input" />
          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
            <Button type="button" variant="outline"
                    onClick={() => fileRef.current?.click()}
                    disabled={busy || committing}
                    data-testid="bill-import-choose">
              <Upload className="w-4 h-4 mr-2" />Choose Excel
            </Button>
            {file && (
              <span className="text-xs text-muted-foreground truncate max-w-[260px]"
                    data-testid="bill-import-filename">{file.name}</span>
            )}
            <button
              type="button"
              onClick={downloadTemplate}
              className="text-xs text-primary hover:underline"
              data-testid="bill-import-template-link"
            >
              Download bills template →
            </button>
          </div>

          {file && !preview && (
            <div>
              <Button onClick={() => runPreview()} disabled={busy}
                      data-testid="bill-import-preview-btn">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analysing…</>
                       : "Preview import plan"}
              </Button>
            </div>
          )}

          {preview && !result && (
            <div className="border border-border p-3 text-xs space-y-2" data-testid="bill-import-preview">
              <div className="grid grid-cols-4 gap-2 text-center">
                <StatChip label="Rows" value={stats.total || 0} tone="muted" />
                <StatChip label="New" value={stats.create || 0} tone="emerald" />
                <StatChip label="Duplicates" value={stats.duplicate || 0} tone="amber" />
                <StatChip label="Errors" value={stats.error || 0} tone="rose" />
              </div>

              {/* Feb 2026 R285 · Inline vendor auto-create panel. */}
              {missingVendors.length > 0 && (
                <div className="border border-amber-400 bg-amber-50 p-2 space-y-1" data-testid="bill-import-missing-vendors">
                  <div className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-amber-700" />
                    <div className="flex-1">
                      <div className="text-[11px] font-bold text-amber-900">
                        {missingVendors.length} vendor{missingVendors.length === 1 ? "" : "s"} not in your directory
                      </div>
                      <div className="text-[10px] text-amber-800">
                        Create them with just the name — you can fill in GSTIN / PAN / address on each vendor's page later.
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={createMissingVendors}
                      disabled={creatingVendors || busy}
                      className="rounded-none bg-amber-600 hover:bg-amber-700 text-white h-7 text-[11px]"
                      data-testid="bill-import-create-missing-vendors"
                    >
                      {creatingVendors
                        ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Creating…</>
                        : `Create ${missingVendors.length} vendor${missingVendors.length === 1 ? "" : "s"}`}
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1 pl-6">
                    {missingVendors.slice(0, 15).map((n) => (
                      <span key={n}
                             className="inline-block text-[10px] border border-amber-300 bg-white px-1.5 py-0.5"
                             data-testid={`bill-import-missing-vendor-${n.slice(0, 20)}`}>{n}</span>
                    ))}
                    {missingVendors.length > 15 && (
                      <span className="text-[10px] text-amber-700">+ {missingVendors.length - 15} more</span>
                    )}
                  </div>
                </div>
              )}

              {/* R289m · Feb 2026 — Vendor auto-link picker (sales flow parity). */}
              {(preview.vendor_matches?.length || 0) > 0 && (
                <VendorLinkPanel
                  matches={preview.vendor_matches}
                  linkMap={linkMap}
                  setLinkMap={setLinkMap}
                />
              )}

              <div className="border border-border max-h-64 overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-muted/40 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1">Row</th>
                      <th className="text-left px-2 py-1">Vendor</th>
                      <th className="text-left px-2 py-1">Bill #</th>
                      <th className="text-right px-2 py-1">Amount</th>
                      <th className="text-left px-2 py-1">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.plan || []).slice(0, 200).map((p) => (
                      <tr key={p.row} className="border-t border-border"
                          data-testid={`bill-import-row-${p.row}`}>
                        <td className="px-2 py-1 font-mono">{p.row}</td>
                        <td className="px-2 py-1 truncate max-w-[160px]">{p.resolved?.vendor_name || "—"}</td>
                        <td className="px-2 py-1 font-mono">{p.resolved?.bill_no || "—"}</td>
                        <td className="px-2 py-1 text-right font-mono">{Number(p.resolved?.amount || 0).toLocaleString()}</td>
                        <td className="px-2 py-1">
                          {p.action === "error"
                            ? <span className="text-rose-700" title={(p.errors || []).join("; ")}>error · {(p.errors || [])[0] || ""}</span>
                            : p.action === "duplicate"
                            ? <span className="text-amber-700">duplicate</span>
                            : <span className="text-emerald-700">new</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(preview.plan || []).length > 200 && (
                  <div className="text-[10px] text-muted-foreground px-2 py-1 italic">…and {(preview.plan || []).length - 200} more rows (all will be committed)</div>
                )}
              </div>
              <div className="flex gap-3 pt-1">
                <label className="flex items-center gap-1 text-[11px]">
                  <input type="checkbox" checked={skipErrors} onChange={(e) => setSkipErrors(e.target.checked)}
                         data-testid="bill-import-skip-errors" /> Skip errors
                </label>
                <label className="flex items-center gap-1 text-[11px]">
                  <input type="checkbox" checked={skipDuplicates} onChange={(e) => setSkipDuplicates(e.target.checked)}
                         data-testid="bill-import-skip-dups" /> Skip duplicates
                </label>
              </div>
            </div>
          )}

          {result && (
            <div className="border border-emerald-400 bg-emerald-50 p-3 text-xs space-y-1"
                 data-testid="bill-import-result">
              <div><b>Created:</b> {result.stats?.created || 0}</div>
              <div><b>Skipped:</b> {result.stats?.skipped || 0}</div>
              <div className="text-[10px] text-emerald-800 mt-1">
                Imported bills land in draft status. Open Bills → click a row to review + post the accounting journal.
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => (result ? onDone() : onClose())} className="w-full sm:w-auto">
            {result ? "Done" : "Cancel"}
          </Button>
          {preview && !result && (
            <Button onClick={commit} disabled={committing}
                    className="w-full sm:w-auto"
                    data-testid="bill-import-commit-btn">
              {committing
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing…</>
                : `Import ${Math.max(0, (stats.create || 0) + (skipDuplicates ? 0 : (stats.duplicate || 0)))} bill${(stats.create || 0) === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function StatChip({ label, value, tone }) {
  const cls = {
    muted: "border-border bg-muted/30 text-foreground",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-800",
    amber: "border-amber-300 bg-amber-50 text-amber-800",
    rose: "border-rose-300 bg-rose-50 text-rose-800",
  }[tone] || "border-border";
  return (
    <div className={`border ${cls} px-2 py-1`}>
      <div className="text-[9px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-lg font-mono font-bold">{value}</div>
    </div>
  );
}



function BillAttachmentPreview({ bill, wsId, onClose, onChanged }) {
  const attachments = bill.attachments || [];
  const [idx, setIdx] = useState(0);
  const [removing, setRemoving] = useState(false);
  const cur = attachments[idx];

  // Feb 2026 · The /files/{id}/download endpoint returns raw bytes and
  // supports `?auth=<jwt>` for `<img src>` and `<iframe src>`. So we
  // just build a direct URL — no roundtrip needed.
  const token = typeof window !== "undefined" ? (localStorage.getItem("co_token") || "") : "";
  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const urlFor = (fid) => `${backendBase}/api/v1/files/${fid}/download?auth=${encodeURIComponent(token)}`;

  if (!cur) {
    onClose?.();
    return null;
  }
  const url = urlFor(cur.file_id);
  const isImg = (cur.content_type || "").startsWith("image/");
  const isPdf = (cur.content_type || "").includes("pdf");

  const removeCurrent = async () => {
    if (!window.confirm(`Remove attachment "${cur.filename}"?`)) return;
    setRemoving(true);
    try {
      await api.delete(`/finance/bills/${bill.id}/attachments/${cur.file_id}`);
      toast.success("Attachment removed");
      onChanged?.();
      if (attachments.length <= 1) {
        onClose?.();
      } else {
        setIdx((i) => Math.max(0, i - 1));
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Remove failed")); }
    finally { setRemoving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden flex flex-col" data-testid="bill-attachment-preview">
        <DialogHeader>
          <DialogTitle className="truncate">{bill.bill_no} · {bill.vendor_name}</DialogTitle>
          <DialogDescription>
            Attachment {idx + 1} of {attachments.length} · <span className="font-mono">{cur.filename}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-auto bg-muted/40 flex items-center justify-center" data-testid="bill-attachment-viewport">
          {isImg ? (
            <img src={url} alt={cur.filename} className="max-w-full max-h-[75vh] object-contain" data-testid="bill-attachment-image" />
          ) : isPdf ? (
            <iframe src={url} title={cur.filename} className="w-full h-[75vh] border-0 bg-background" data-testid="bill-attachment-pdf" />
          ) : (
            <div className="text-sm text-muted-foreground p-8 text-center space-y-3">
              <FileText className="w-10 h-10 mx-auto opacity-40" />
              <div>Preview isn't available for <code>{cur.content_type || "this file type"}</code>.</div>
              <a href={url} target="_blank" rel="noopener noreferrer" className="inline-block text-primary hover:underline text-sm">
                Open in new tab
              </a>
            </div>
          )}
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
          <div className="flex items-center gap-1 flex-wrap">
            {attachments.map((a, i) => (
              <button
                key={a.file_id}
                type="button"
                onClick={() => setIdx(i)}
                className={`text-[10px] px-2 py-1 border ${i === idx ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
                title={a.filename}
                data-testid={`bill-attachment-thumb-${i}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <a href={url} target="_blank" rel="noopener noreferrer" download={cur.filename} className="inline-flex" data-testid="bill-attachment-download">
              <Button variant="outline" size="sm" type="button"><Download className="w-3.5 h-3.5 mr-1" />Download</Button>
            </a>
            <Button variant="ghost" size="sm" onClick={removeCurrent} disabled={removing} data-testid="bill-attachment-remove" className="text-destructive hover:bg-destructive/10">
              {removing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5 mr-1" />}Remove
            </Button>
            <Button size="sm" onClick={onClose}>Close</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BillAttachmentsPicker({ pending, uploading, ocrBusy, onUpload, onRemove }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const handlePick = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) onUpload(files);
    // Reset so picking the same file twice re-fires onChange.
    e.target.value = "";
  };
  const humanSize = (n) => {
    if (!n) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };
  const isImage = (ct) => (ct || "").startsWith("image/");
  return (
    <div className="space-y-2" data-testid="bill-attachments-picker">
      <input ref={fileRef} type="file" multiple hidden onChange={handlePick} data-testid="bill-attachments-file-input" />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={handlePick} data-testid="bill-attachments-camera-input" />
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          type="button" variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full sm:w-auto"
          data-testid="bill-attachments-choose-file"
        >
          <Paperclip className="w-4 h-4 mr-2" />Choose file
        </Button>
        <Button
          type="button" variant="outline"
          onClick={() => cameraRef.current?.click()}
          disabled={uploading}
          className="w-full sm:w-auto"
          data-testid="bill-attachments-take-photo"
        >
          <Camera className="w-4 h-4 mr-2" />Take photo
        </Button>
        {uploading && (
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1 self-center">
            <Loader2 className="w-3 h-3 animate-spin" />Uploading…
          </span>
        )}
        {ocrBusy && !uploading && (
          <span className="text-xs text-primary inline-flex items-center gap-1 self-center" data-testid="bill-ocr-busy">
            <Loader2 className="w-3 h-3 animate-spin" />Reading photo…
          </span>
        )}
      </div>
      {pending.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic border border-dashed border-border px-3 py-2">
          No attachments yet. Upload the scanned bill or capture a photo of it.
        </div>
      ) : (
        <ul className="divide-y divide-border border border-border" data-testid="bill-attachments-list">
          {pending.map((a) => (
            <li key={a.file_id} className="flex items-center gap-2 px-2 py-1.5 text-xs" data-testid={`bill-attachment-${a.file_id}`}>
              {isImage(a.content_type) ? (
                <div className="w-8 h-8 bg-muted flex items-center justify-center overflow-hidden shrink-0">
                  <Camera className="w-4 h-4 text-muted-foreground" />
                </div>
              ) : (
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{a.filename || "file"}</div>
                <div className="text-[10px] text-muted-foreground">{a.content_type || "unknown"} · {humanSize(a.size)}</div>
              </div>
              <button
                type="button"
                onClick={() => onRemove(a.file_id)}
                className="p-1 hover:bg-destructive/10 hover:text-destructive rounded"
                aria-label="Remove attachment"
                data-testid={`bill-attachment-remove-${a.file_id}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function BulkPayDialog({ open, onOpenChange, count, totalOutstanding, payAccts, onConfirm }) {
  const [acctId, setAcctId] = useState("");
  const [method, setMethod] = useState("bank");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bills-bulk-pay-dialog">
        <DialogHeader>
          <DialogTitle>Mark {count} bill{count === 1 ? "" : "s"} paid</DialogTitle>
          <DialogDescription>
            Records a payment for each bill&apos;s full outstanding amount ({fmt(totalOutstanding, "INR")}) using the same bank/cash account and method.
            Each payment posts its own balanced journal — the audit trail matches single-bill payments.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Payment account</Label>
            <Select value={acctId} onValueChange={setAcctId}>
              <SelectTrigger data-testid="bills-bulk-pay-account"><SelectValue placeholder="Bank / cash account" /></SelectTrigger>
              <SelectContent>{payAccts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="bills-bulk-pay-method"><SelectValue /></SelectTrigger>
              <SelectContent>{["bank", "cash", "upi", "cheque", "card", "other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!acctId || count === 0} onClick={() => onConfirm(acctId, method)} data-testid="bills-bulk-pay-confirm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            Pay {count} · {fmt(totalOutstanding, "INR")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PaymentsTab({ wsId }) {
  const [payments, setPayments] = useState([]);
  const [kind, setKind] = useState("");
  useEffect(() => {
    if (!wsId) return;
    const q = kind ? `?kind=${kind}` : "";
    api.get(`/finance/workspaces/${wsId}/payments${q}`).then((r) => setPayments(r.data.payments || []));
  }, [wsId, kind]);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Label>Filter</Label>
        <Select value={kind || "all"} onValueChange={(v) => setKind(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="payments-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="AR">AR (customer)</SelectItem><SelectItem value="AP">AP (vendor)</SelectItem></SelectContent>
        </Select>
        <div className="text-sm text-muted-foreground ml-auto">{payments.length} payments</div>
      </div>
      <Card><CardContent className="p-0">
        <AdvancedTable
          tableKey="payments"
          title="Payments"
          rows={payments}
          columns={[
            { key: "payment_date", label: "Date", width: 120, sortable: true, filterType: "date-range", frozen: true },
            { key: "kind", label: "Kind", width: 90, sortable: true, groupable: true, filterType: "select",
              filterOptions: [{ value: "AR", label: "AR (customer)" }, { value: "AP", label: "AP (vendor)" }],
              render: (p) => <Badge variant="outline">{p.kind}</Badge> },
            { key: "method", label: "Method", width: 110, sortable: true, groupable: true, filterType: "text",
              render: (p) => <span className="text-xs uppercase">{p.method}</span> },
            { key: "reference", label: "Reference", width: 180, filterType: "text",
              accessor: (p) => p.bill_id || p.invoice_id || "",
              render: (p) => <span className="text-xs font-mono">{p.bill_id || p.invoice_id || "—"}</span> },
            { key: "amount", label: "Amount", width: 140, sortable: true, align: "right", type: "currency", sum: true, filterType: "number-range",
              accessor: (p) => Number(p.amount || 0),
              render: (p) => <span className="font-mono">{fmt(p.amount)}</span> },
          ]}
          emptyTitle="No payments"
          emptyDescription="Payments recorded against invoices or bills will appear here."
        />
      </CardContent></Card>
    </div>
  );
}


/* ─── Feb 2026 · AP Dashboard tab ─────────────────────────────────
   Read-only KPIs + ageing buckets + top overdue vendors + weekly
   cash-out forecast. Wraps the `/workspaces/{ws}/ap-dashboard`
   endpoint. Kept lean — no charts library, no server-round-tripping
   filters. If we need drill-down later, link out to /bills. */
function ApDashboardTab({ wsId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payVendor, setPayVendor] = useState(null); // Pay-now dialog
  const [emailVendor, setEmailVendor] = useState(null); // Email statement dialog
  // Nov 2026 · Augmentation — new /finance/.../ap-dashboard endpoint
  // with monthly series + top vendors + by-category + aging buckets.
  // Rendered as a charts strip above the existing payables grid.
  const [charts, setCharts] = useState(null);
  const load = () => {
    if (!wsId) return;
    setBusy(true);
    api.get(`/workspaces/${wsId}/ap-dashboard`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Load failed")))
      .finally(() => setBusy(false));
    api.get(`/finance/workspaces/${wsId}/ap-dashboard`, { params: { months: 12 } })
      .then((r) => setCharts(r.data))
      .catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);

  if (!data) return <div className="text-sm text-muted-foreground">{busy ? "Loading…" : "No data"}</div>;
  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  const totals = data.totals || {};
  const ageing = data.ageing || {};
  const ageingCount = data.ageing_count || {};
  const bandDefs = [
    { key: "current", label: "Not due", tone: "bg-emerald-50 border-emerald-300 text-emerald-800" },
    { key: "d0_30",   label: "0–30 d overdue", tone: "bg-amber-50 border-amber-300 text-amber-800" },
    { key: "d31_60",  label: "31–60 d overdue", tone: "bg-orange-50 border-orange-300 text-orange-800" },
    { key: "d61_90",  label: "61–90 d overdue", tone: "bg-rose-50 border-rose-300 text-rose-800" },
    { key: "d90_plus", label: "90 d+ overdue", tone: "bg-red-50 border-red-400 text-red-900" },
  ];
  const maxCashOut = Math.max(1, ...(data.cash_out_next_30d || []).map((r) => r.amount));

  return (
    <div className="space-y-6" data-testid="ap-dashboard-tab">
      {/* Nov 2026 · Charts strip — spend trend, top vendors, by category,
          aging buckets, reimbursable KPIs. Driven by new /finance/.../ap-dashboard endpoint. */}
      {charts && (
        <div className="space-y-4" data-testid="ap-charts-strip">
          {/* KPI tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Card><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Outstanding</div>
              <div className="text-xl font-mono font-bold text-red-700">{fmt(charts.kpis?.outstanding, "INR")}</div>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">This Month</div>
              <div className="text-xl font-mono font-bold">{fmt(charts.kpis?.this_month_total, "INR")}</div>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Reimb. Pending</div>
              <div className="text-xl font-mono font-bold text-amber-700">{fmt(charts.kpis?.reimbursable_pending_total, "INR")}</div>
              <div className="text-[10px] text-muted-foreground">{charts.kpis?.reimbursable_pending_count || 0} bills · uninvoiced</div>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Awaiting Client</div>
              <div className="text-xl font-mono font-bold text-blue-700">{fmt(charts.kpis?.reimbursement_awaited_total, "INR")}</div>
              <div className="text-[10px] text-muted-foreground">{charts.kpis?.reimbursement_awaited_count || 0} invoices raised</div>
            </CardContent></Card>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Monthly bills trend */}
            <Card><CardContent className="p-3">
              <div className="text-xs font-semibold mb-2">12-month spend trend</div>
              <div className="w-full h-64" data-testid="ap-chart-trend">
                <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <BarChart data={charts.monthly || []} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 100000 ? `${(v/100000).toFixed(1)}L` : v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <Tooltip formatter={(v) => fmt(v, "INR")} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="paid" stackId="a" fill="#16a34a" name="Paid" />
                    <Bar dataKey="outstanding" stackId="a" fill="#f59e0b" name="Outstanding" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent></Card>
            {/* Top vendors */}
            <Card><CardContent className="p-3">
              <div className="text-xs font-semibold mb-2">Top 10 vendors (12 months)</div>
              <div className="w-full h-64" data-testid="ap-chart-vendors">
                <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <BarChart data={charts.by_vendor_top10 || []} layout="vertical" margin={{ top: 8, right: 8, bottom: 8, left: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 100000 ? `${(v/100000).toFixed(1)}L` : v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <YAxis type="category" dataKey="vendor_name" tick={{ fontSize: 10 }} width={100} />
                    <Tooltip formatter={(v) => fmt(v, "INR")} />
                    <Bar dataKey="total" fill="#2563eb" name="Spend" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent></Card>
            {/* Status breakdown */}
            <Card><CardContent className="p-3">
              <div className="text-xs font-semibold mb-2">Status breakdown</div>
              <div className="divide-y divide-border" data-testid="ap-chart-status">
                {(charts.by_status || []).map((r) => (
                  <div key={r.status} className="py-1.5 flex items-center justify-between text-xs">
                    <Badge variant={r.status === "paid" ? "default" : "secondary"} className="capitalize">{r.status}</Badge>
                    <div className="font-mono">
                      <span className="text-muted-foreground">{r.count} · </span>
                      <span className="font-semibold">{fmt(r.total, "INR")}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent></Card>
            {/* Aging */}
            <Card><CardContent className="p-3">
              <div className="text-xs font-semibold mb-2">Aging buckets (open + partial)</div>
              <div className="w-full h-64" data-testid="ap-chart-aging">
                <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <BarChart data={charts.aging || []} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 100000 ? `${(v/100000).toFixed(1)}L` : v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <Tooltip formatter={(v) => fmt(v, "INR")} />
                    <Bar dataKey="total" fill="#dc2626" name="Outstanding" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent></Card>
          </div>
          {/* By-category summary table */}
          {(charts.by_category || []).length > 0 && (
            <Card><CardContent className="p-3">
              <div className="text-xs font-semibold mb-2">Spend by category (12 months)</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="ap-chart-cat-table">
                  <thead className="bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <tr><th className="text-left px-2 py-1.5">Category</th>
                        <th className="text-right px-2 py-1.5">Bills</th>
                        <th className="text-right px-2 py-1.5">Total</th></tr>
                  </thead>
                  <tbody>
                    {(charts.by_category || []).slice(0, 15).map((r) => (
                      <tr key={r.category_id || "none"} className="border-t border-border">
                        <td className="px-2 py-1.5">{r.name}</td>
                        <td className="px-2 py-1.5 text-right">{r.count}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{fmt(r.total, "INR")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent></Card>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
          As of {data.as_of}
        </div>
        <Button size="sm" variant="outline" className="rounded-none h-8" onClick={load} disabled={busy}>
          {busy ? "Refreshing…" : "Refresh"}
        </Button>
      </div>
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <ApKpi label="AP outstanding" value={inr(totals.ap_outstanding)} sub={`${totals.bills_open || 0} bills open`} testid="ap-kpi-outstanding" />
        <ApKpi label="Overdue" value={inr(totals.ap_overdue)} sub={`${totals.bills_overdue || 0} overdue`} tone="text-rose-700" testid="ap-kpi-overdue" />
        <ApKpi label="On-time cover"
                value={totals.ap_outstanding
                  ? `${Math.round(100 - (totals.ap_overdue / totals.ap_outstanding) * 100)}%`
                  : "—"}
                sub="Non-overdue share" tone="text-emerald-700" testid="ap-kpi-ontime" />
        <ApKpi label="Avg ticket" value={totals.bills_open
                  ? inr(totals.ap_outstanding / totals.bills_open) : "—"}
                sub="Outstanding ÷ bills" testid="ap-kpi-avg" />
      </div>
      {/* Ageing bands */}
      <div>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">
          Ageing buckets
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {bandDefs.map((b) => (
            <div key={b.key} data-testid={`ap-bucket-${b.key}`}
                  className={`border p-3 ${b.tone}`}>
              <div className="text-[10px] uppercase tracking-widest font-semibold">{b.label}</div>
              <div className="text-lg font-bold font-mono mt-1">{inr(ageing[b.key])}</div>
              <div className="text-[11px] opacity-80">{ageingCount[b.key] || 0} bills</div>
            </div>
          ))}
        </div>
      </div>
      {/* Top overdue vendors */}
      <div>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">
          Top overdue vendors
        </div>
        {(data.top_overdue_vendors || []).length === 0
          ? <div className="text-sm text-muted-foreground italic">No overdue vendors 🎉</div>
          : (
          <div className="border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 uppercase tracking-widest text-[9px] font-semibold">
                <tr>
                  <th className="px-3 py-1.5 text-left">Vendor</th>
                  <th className="px-3 py-1.5 text-right">Overdue</th>
                  <th className="px-3 py-1.5 text-right">Outstanding</th>
                  <th className="px-3 py-1.5 text-right">Bills</th>
                  <th className="px-3 py-1.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data.top_overdue_vendors || []).map((v) => (
                  <tr key={v.vendor_id} className="border-t border-border">
                    <td className="px-3 py-1.5 font-semibold">{v.vendor_name}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-rose-700">{inr(v.overdue)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{inr(v.outstanding)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{v.count}</td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      {/* Feb 2026 · Pay now — opens a per-vendor
                          Record-Payment dialog listing that vendor's
                          overdue bills so CFOs settle them in one tap
                          (no navigation). */}
                      <Button size="sm" className="rounded-none h-6 text-[10px] mr-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                               onClick={() => setPayVendor(v)}
                               data-testid={`ap-pay-now-${v.vendor_id}`}>
                        <Send className="w-3 h-3 mr-1" />Pay now
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-none h-6 text-[10px] mr-1"
                               onClick={() => setEmailVendor(v)}
                               title="Email statement to vendor"
                               data-testid={`ap-email-${v.vendor_id}`}>
                        <Mail className="w-3 h-3 mr-1" />Email
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-none h-6 text-[10px]"
                               onClick={() => downloadXlsxAuth(
                                 `/workspaces/${wsId}/vendors/${v.vendor_id}/statement.xlsx`,
                                 `${(v.vendor_name || "vendor").replace(/[^\w-]+/g, "_")}-statement.xlsx`)}
                               title="Download vendor statement"
                               data-testid={`ap-statement-${v.vendor_id}`}>
                        Statement
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* 30-day cash-out forecast */}
      {(data.cash_out_next_30d || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">
            Cash-out forecast · next 30 days
          </div>
          <div className="space-y-1" data-testid="ap-cash-out-chart">
            {data.cash_out_next_30d.map((r) => (
              <div key={r.iso_week} className="flex items-center gap-3 text-xs">
                <div className="w-16 font-mono text-muted-foreground">{r.iso_week}</div>
                <div className="flex-1 bg-muted h-5 relative">
                  <div className="absolute inset-y-0 left-0 bg-primary/80"
                        style={{ width: `${(r.amount / maxCashOut) * 100}%` }} />
                </div>
                <div className="w-24 text-right font-mono">{inr(r.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Feb 2026 · Pay-now dialog — lists the vendor's overdue bills
          and lets the CFO clear them in one tap via bulk-mark-paid. */}
      <ApPayNowDialog
        wsId={wsId}
        vendor={payVendor}
        onClose={() => setPayVendor(null)}
        onDone={() => { setPayVendor(null); load(); }}
      />
      {/* Feb 2026 · Email-statement dialog — dispatches the Excel +
          inline HTML summary to the vendor's on-file email. */}
      <ApEmailStatementDialog
        wsId={wsId}
        vendor={emailVendor}
        onClose={() => setEmailVendor(null)}
        onSent={() => setEmailVendor(null)}
      />
    </div>
  );
}


/* ─── Pay-now dialog ────────────────────────────────────────────
   Reused endpoint: `/finance/workspaces/{ws}/bills/bulk-mark-paid`.
   Loads the vendor's open bills, defaults to selecting overdue-only,
   and posts a single call so each payment gets its own balanced
   journal entry. */
function ApPayNowDialog({ wsId, vendor, onClose, onDone }) {
  const [bills, setBills] = useState([]);
  const [payAccts, setPayAccts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [acctId, setAcctId] = useState("");
  const [method, setMethod] = useState("bank");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!vendor || !wsId) return;
    setLoading(true);
    Promise.all([
      api.get(`/finance/workspaces/${wsId}/bills`),
      api.get(`/finance/workspaces/${wsId}/accounts`),
    ]).then(([b, a]) => {
      const today = new Date().toISOString().slice(0, 10);
      const own = (b.data.bills || [])
        .filter((x) => x.vendor_id === vendor.vendor_id
          && x.status !== "paid"
          && Number(x.outstanding || 0) > 0.01);
      setBills(own);
      // Pre-select overdue rows
      const overdueIds = new Set(own.filter((x) => (x.due_date || x.bill_date) < today).map((x) => x.id));
      setSelected(overdueIds.size ? overdueIds : new Set(own.map((x) => x.id)));
      const accounts = (a.data.accounts || []).filter((x) => ["bank", "cash"].includes(x.subtype));
      setPayAccts(accounts);
      if (accounts.length && !acctId) setAcctId(accounts[0].id);
    }).catch((e) => toast.error(apiErrorMessage(e, "Load failed")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [vendor, wsId]);
  const toggle = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const totalSel = useMemo(
    () => bills.filter((b) => selected.has(b.id)).reduce((n, b) => n + Number(b.outstanding || 0), 0),
    [bills, selected],
  );
  const confirm = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !acctId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/finance/workspaces/${wsId}/bills/bulk-mark-paid`, {
        bill_ids: ids,
        payment_date: new Date().toISOString().slice(0, 10),
        payment_account_id: acctId,
        method,
      });
      toast.success(`Paid ${data.paid} bill(s) · ₹${data.total_amount.toLocaleString("en-IN")}${data.skipped ? ` · skipped ${data.skipped}` : ""}`);
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Payment failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!vendor} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ap-pay-now-dialog">
        <DialogHeader>
          <DialogTitle>Pay {vendor?.vendor_name}</DialogTitle>
          <DialogDescription>
            Select bills to clear. Each bill records its own balanced journal — the audit trail matches single-bill payments 1:1.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-muted-foreground py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading bills…</div>
        ) : bills.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No open bills for this vendor.</div>
        ) : (
          <div className="space-y-3">
            <div className="border border-border max-h-[36vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 uppercase tracking-widest text-[9px] font-semibold sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 w-8"></th>
                    <th className="px-2 py-1.5 text-left">Bill #</th>
                    <th className="px-2 py-1.5 text-left">Date</th>
                    <th className="px-2 py-1.5 text-left">Due</th>
                    <th className="px-2 py-1.5 text-right">Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map((b) => {
                    const today = new Date().toISOString().slice(0, 10);
                    const overdue = (b.due_date || b.bill_date) < today;
                    return (
                      <tr key={b.id} className={`border-t border-border ${selected.has(b.id) ? "bg-emerald-50" : ""}`}>
                        <td className="px-2 py-1.5">
                          <input type="checkbox" checked={selected.has(b.id)} onChange={() => toggle(b.id)} data-testid={`ap-pay-bill-${b.id}`} />
                        </td>
                        <td className="px-2 py-1.5 font-mono">{b.bill_no}</td>
                        <td className="px-2 py-1.5">{b.bill_date}</td>
                        <td className={`px-2 py-1.5 ${overdue ? "text-rose-700 font-semibold" : ""}`}>{b.due_date || "—"}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{fmt(b.outstanding, b.currency)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Payment account</Label>
                <Select value={acctId} onValueChange={setAcctId}>
                  <SelectTrigger data-testid="ap-pay-account"><SelectValue placeholder="Bank / cash" /></SelectTrigger>
                  <SelectContent>{payAccts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="ap-pay-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{["bank", "cash", "upi", "cheque", "card", "other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={busy || !acctId || selected.size === 0}
            onClick={confirm}
            data-testid="ap-pay-confirm">
            {busy ? "Recording…" : `Record payment · ₹${totalSel.toLocaleString("en-IN")}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Email statement dialog ────────────────────────────────────
   Posts to `/workspaces/{ws}/vendors/{vid}/statement/email` which
   emails the Excel + inline HTML summary via the workspace's
   configured mail provider (Resend / ZeptoMail). */
function ApEmailStatementDialog({ wsId, vendor, onClose, onSent }) {
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [vendorEmail, setVendorEmail] = useState("");
  useEffect(() => {
    if (!vendor || !wsId) return;
    api.get(`/finance/workspaces/${wsId}/vendors`)
      .then((r) => {
        const v = (r.data.vendors || []).find((x) => x.id === vendor.vendor_id);
        const email = v?.contact_email || v?.email || "";
        setVendorEmail(email);
        setTo(email);
        setNote("");
      });
  }, [vendor, wsId]);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/vendors/${vendor.vendor_id}/statement/email`, { to, note });
      toast.success(`Statement emailed to ${to}`);
      onSent();
    } catch (e) { toast.error(apiErrorMessage(e, "Email failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!vendor} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="ap-email-statement-dialog">
        <DialogHeader>
          <DialogTitle>Email statement · {vendor?.vendor_name}</DialogTitle>
          <DialogDescription>
            Sends the vendor a statement with inline HTML summary + Excel attachment (Summary + Bills + Payments).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Recipient email</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="vendor@example.com" data-testid="ap-email-to" />
            {vendorEmail && to !== vendorEmail && (
              <div className="text-[10px] text-muted-foreground mt-1">On-file: {vendorEmail}</div>
            )}
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Kindly clear outstanding by Fri." data-testid="ap-email-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={busy || !to} data-testid="ap-email-send">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : <><Mail className="w-4 h-4 mr-2" />Send</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ApKpi({ label, value, sub, tone = "", testid }) {
  return (
    <div className="border border-border bg-background p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold font-mono mt-1 ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}



// R289m · Feb 2026 — Vendor auto-link picker (mirrors ImportWizard's
// customer panel). One row per distinct vendor name in the file;
// user picks link / create / rename per row.
function VendorLinkPanel({ matches, linkMap, setLinkMap }) {
  const setRow = (key, patch) => {
    setLinkMap((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  };
  const badgeFor = (conf) => ({
    exact: ["Auto-linked", "bg-emerald-100 text-emerald-800 border-emerald-300"],
    partial: ["Likely match", "bg-amber-100 text-amber-800 border-amber-300"],
    none: ["No match", "bg-rose-100 text-rose-800 border-rose-300"],
  }[conf] || ["Unknown", "bg-muted"]);
  const needsAction = matches.filter((m) => m.confidence === "none" || m.confidence === "partial").length;

  return (
    <div className="border border-primary/30 bg-primary/5 rounded overflow-hidden" data-testid="bill-vendor-link-panel">
      <div className="px-3 py-2 border-b border-primary/20 flex items-center gap-2 flex-wrap">
        <strong className="text-xs uppercase tracking-widest">Vendor link</strong>
        <span className="text-[11px] text-muted-foreground flex-1">
          {matches.length} unmatched vendor{matches.length === 1 ? "" : "s"} in the file
          {needsAction > 0 ? ` · ${needsAction} need${needsAction === 1 ? "s" : ""} your attention` : ""}
        </span>
      </div>
      <div className="max-h-64 overflow-y-auto divide-y divide-primary/10">
        {matches.map((m) => {
          const [label, cls] = badgeFor(m.confidence);
          const cur = linkMap[m.raw_name] || {};
          const action = cur.action || (m.matched_vendor ? "link" : "create");
          return (
            <div key={m.raw_name} className="px-3 py-2 text-xs grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 items-center" data-testid={`bill-vendor-row-${m.raw_name}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{m.raw_name || "(blank)"}</span>
                  <span className={`text-[10px] uppercase tracking-wider border rounded px-1 py-0.5 ${cls}`}>{label}</span>
                </div>
                {m.matched_vendor && (
                  <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                    Best match: <span className="text-foreground">{m.matched_vendor.name}</span>
                    {m.matched_vendor.gstin && <span className="ml-1 font-mono">({m.matched_vendor.gstin})</span>}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                <select
                  className="text-xs border border-border bg-background rounded px-1 py-1"
                  value={action}
                  onChange={(e) => setRow(m.raw_name, { action: e.target.value })}
                  data-testid={`bill-vendor-action-${m.raw_name}`}
                >
                  <option value="link">Link to existing…</option>
                  <option value="create">Create new vendor</option>
                </select>
                {action === "link" && (
                  <select
                    className="text-xs border border-border bg-background rounded px-1 py-1 max-w-[200px]"
                    value={cur.vendor_id || m.matched_vendor?.id || ""}
                    onChange={(e) => setRow(m.raw_name, { vendor_id: e.target.value })}
                    data-testid={`bill-vendor-link-${m.raw_name}`}
                  >
                    {[
                      ...(m.matched_vendor ? [m.matched_vendor] : []),
                      ...(m.suggestions || []).filter((s) => s.id !== m.matched_vendor?.id),
                    ].map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                    {!m.matched_vendor && (m.suggestions?.length || 0) === 0 && (
                      <option value="">No suggestions</option>
                    )}
                  </select>
                )}
                {action === "create" && (
                  <input
                    type="text"
                    className="text-xs border border-border bg-background rounded px-2 py-1 max-w-[200px]"
                    placeholder="New vendor name"
                    value={cur.new_name || m.raw_name}
                    onChange={(e) => setRow(m.raw_name, { new_name: e.target.value })}
                    data-testid={`bill-vendor-newname-${m.raw_name}`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}



// ═══════════════════════ Nov 2026 · Recurring vendor bills ═══════════════════════

function RecurringTab({ wsId }) {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [accts, setAccts] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const empty = {
    vendor_id: "", label: "", est_amount: 0, currency: "INR",
    frequency: "monthly", day_of_month: 1,
    category_account_id: "", sub_account_id: "",
    cgst_pct: 0, sgst_pct: 0, igst_pct: 0,
    tds_section: "", tds_rate: 0,
    start_month: new Date().toISOString().slice(0, 7),
    end_month: "", notes: "", is_active: true,
  };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const load = () => api.get(`/finance/workspaces/${wsId}/recurring-bills`).then((r) => setRows(r.data.recurring_bills || []));
  useEffect(() => {
    if (!wsId) return;
    load();
    api.get(`/finance/workspaces/${wsId}/vendors`).then((r) => setVendors(r.data.vendors || []));
    api.get(`/finance/workspaces/${wsId}/accounts`).then((r) => setAccts(r.data.accounts || []));
  }, [wsId]);
  const expAccts = accts.filter((a) => a.type === "expense");
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ ...empty, ...r, sub_account_id: r.sub_account_id || "",
              end_month: r.end_month || "", notes: r.notes || "" });
    setEditId(r.id);
    setOpen(true);
  };
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const p = {
        ...form,
        est_amount: Number(form.est_amount),
        cgst_pct: Number(form.cgst_pct),
        sgst_pct: Number(form.sgst_pct),
        igst_pct: Number(form.igst_pct),
        tds_rate: Number(form.tds_rate),
        day_of_month: Number(form.day_of_month),
        sub_account_id: form.sub_account_id || null,
        end_month: form.end_month || null,
        notes: form.notes || null,
      };
      if (editId) {
        await api.patch(`/finance/workspaces/${wsId}/recurring-bills/${editId}`, p);
        toast.success("Template updated");
      } else {
        await api.post(`/finance/workspaces/${wsId}/recurring-bills`, p);
        toast.success("Recurring template created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this recurring template? Already-proposed bills will stay.")) return;
    try {
      await api.delete(`/finance/workspaces/${wsId}/recurring-bills/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  const generateNow = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post(`/finance/workspaces/${wsId}/recurring-bills/generate-proposals`);
      toast.success(`Generated ${data.created} proposed bill(s)${data.skipped_existing ? ` · skipped ${data.skipped_existing} already-proposed` : ""}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setGenBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="recurring-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm text-muted-foreground">{rows.length} recurring template{rows.length === 1 ? "" : "s"}</div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={generateNow} disabled={genBusy} data-testid="recurring-generate-now">
            {genBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Repeat className="w-4 h-4 mr-1" />}
            Generate upcoming
          </Button>
          <Button size="sm" onClick={openNew} data-testid="recurring-new">
            <Plus className="w-4 h-4 mr-1" />New template
          </Button>
        </div>
      </div>
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs md:text-sm">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Label</th>
                <th className="text-left px-3 py-2 hidden sm:table-cell">Vendor</th>
                <th className="text-right px-3 py-2">Est. amount</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Frequency</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Start → End</th>
                <th className="text-center px-3 py-2 hidden sm:table-cell">Active</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7} className="text-center text-muted-foreground py-8">No recurring templates yet. Create one to auto-propose monthly / quarterly / yearly vendor bills.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-muted/30" data-testid={`recurring-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium">
                    <div>{r.label}</div>
                    <div className="text-[10px] text-muted-foreground sm:hidden">{r.vendor_name}</div>
                  </td>
                  <td className="px-3 py-2 hidden sm:table-cell">{r.vendor_name}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(r.est_amount, r.currency)}</td>
                  <td className="px-3 py-2 hidden md:table-cell capitalize">{r.frequency} · day {r.day_of_month}</td>
                  <td className="px-3 py-2 hidden md:table-cell text-xs">{r.start_month} → {r.end_month || "ongoing"}</td>
                  <td className="px-3 py-2 text-center hidden sm:table-cell">
                    <Badge variant={r.is_active ? "default" : "secondary"}>{r.is_active ? "yes" : "no"}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Button size="sm" variant="outline" className="h-7" onClick={() => openEdit(r)} data-testid={`recurring-edit-${r.id}`}>Edit</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => remove(r.id)} data-testid={`recurring-delete-${r.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="recurring-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit recurring template" : "New recurring template"}</DialogTitle>
            <DialogDescription>A nightly job will create a <b>proposed</b> bill for each cycle. Confirm each proposed row when the real vendor bill arrives — the amount can be edited at confirm time.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Label *</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="recurring-label" placeholder="Office rent — Mumbai" className="h-10" /></div>
            <div><Label>Vendor *</Label>
              <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger data-testid="recurring-vendor" className="h-10"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Est. amount *</Label><Input inputMode="decimal" type="number" step="0.01" value={form.est_amount} onChange={(e) => setForm({ ...form, est_amount: e.target.value })} data-testid="recurring-amount" className="h-10 font-mono" /></div>
            <div><Label>Frequency *</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger data-testid="recurring-freq" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Day of month</Label><Input inputMode="numeric" type="number" min="1" max="28" value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} data-testid="recurring-dom" className="h-10 font-mono" /></div>
            <div><Label>Start month *</Label><Input type="month" value={form.start_month} onChange={(e) => setForm({ ...form, start_month: e.target.value })} data-testid="recurring-start" className="h-10" /></div>
            <div><Label>End month (optional)</Label><Input type="month" value={form.end_month} onChange={(e) => setForm({ ...form, end_month: e.target.value })} data-testid="recurring-end" className="h-10" /></div>
            <div><Label>Category (expense account) *</Label>
              <Select value={form.category_account_id} onValueChange={(v) => setForm({ ...form, category_account_id: v, sub_account_id: "" })}>
                <SelectTrigger data-testid="recurring-cat" className="h-10"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{expAccts.filter((a) => !a.parent_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Sub-category (optional)</Label>
              <Select value={form.sub_account_id} onValueChange={(v) => setForm({ ...form, sub_account_id: v })}>
                <SelectTrigger data-testid="recurring-sub" className="h-10"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{expAccts.filter((a) => a.parent_id === form.category_account_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>CGST %</Label><Input inputMode="decimal" type="number" step="0.01" value={form.cgst_pct} onChange={(e) => setForm({ ...form, cgst_pct: e.target.value })} className="h-10 font-mono" /></div>
            <div><Label>SGST %</Label><Input inputMode="decimal" type="number" step="0.01" value={form.sgst_pct} onChange={(e) => setForm({ ...form, sgst_pct: e.target.value })} className="h-10 font-mono" /></div>
            <div><Label>IGST %</Label><Input inputMode="decimal" type="number" step="0.01" value={form.igst_pct} onChange={(e) => setForm({ ...form, igst_pct: e.target.value })} className="h-10 font-mono" /></div>
            <div><Label>TDS rate %</Label><Input inputMode="decimal" type="number" step="0.01" value={form.tds_rate} onChange={(e) => setForm({ ...form, tds_rate: e.target.value })} className="h-10 font-mono" /></div>
            <div className="sm:col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-10" /></div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} id="rc-active" data-testid="recurring-active" />
              <Label htmlFor="rc-active" className="text-xs cursor-pointer">Active (generates proposed bills on schedule)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !form.vendor_id || !form.label || !form.category_account_id || !form.est_amount} data-testid="recurring-submit">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : (editId ? "Save changes" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ═══════════════════════ Nov 2026 · Annual projections ═══════════════════════

function ProjectionsTab({ wsId }) {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [mode, setMode] = useState("recurring"); // 'recurring' | 'trailing12'
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/finance/workspaces/${wsId}/bills/annual-projections`, { params: { year, mode: "both" } });
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (wsId) load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId, year]);
  // Aggregate rows into { month: {catName: amount} } for the chart
  const { chartData, categories, grandTotal, actualsTotal, projectedTotal } = useMemo(() => {
    if (!data) return { chartData: [], categories: [], grandTotal: 0, actualsTotal: 0, projectedTotal: 0 };
    const accById = {};
    (data.accounts || []).forEach((a) => { accById[a.id] = a; });
    const projRows = mode === "recurring" ? (data.recurring || []) : (data.trailing12 || []);
    const all = [...(data.actuals || []).map((x) => ({ ...x, kind: "actual" })),
                 ...projRows.map((x) => ({ ...x, kind: "projected" }))];
    const byMonth = {};
    const catNames = new Set();
    let actualsT = 0, projT = 0;
    for (const r of all) {
      const catAcct = accById[r.category_id];
      const name = catAcct ? (catAcct.name || catAcct.code) : "Uncategorised";
      catNames.add(name);
      if (!byMonth[r.month]) byMonth[r.month] = { month: r.month };
      byMonth[r.month][name] = (byMonth[r.month][name] || 0) + Number(r.amount || 0);
      if (r.kind === "actual") actualsT += Number(r.amount || 0);
      else projT += Number(r.amount || 0);
    }
    const chartRows = (data.months || []).map((m) => byMonth[m] || { month: m });
    return { chartData: chartRows, categories: Array.from(catNames),
             grandTotal: actualsT + projT, actualsTotal: actualsT, projectedTotal: projT };
  }, [data, mode]);
  // Palette (10 distinct categories; wraps if more).
  const palette = ["#2563eb", "#16a34a", "#dc2626", "#f59e0b", "#9333ea", "#0891b2", "#db2777", "#65a30d", "#4b5563", "#ea580c"];
  // Table pivot: rows = (category, sub-cat), cols = months.
  const tableRows = useMemo(() => {
    if (!data) return [];
    const accById = {};
    (data.accounts || []).forEach((a) => { accById[a.id] = a; });
    const projRows = mode === "recurring" ? (data.recurring || []) : (data.trailing12 || []);
    const all = [...(data.actuals || []).map((x) => ({ ...x, kind: "actual" })),
                 ...projRows.map((x) => ({ ...x, kind: "projected" }))];
    const map = {};
    for (const r of all) {
      const key = `${r.category_id || "none"}::${r.sub_account_id || "none"}`;
      if (!map[key]) {
        map[key] = { key,
          category: accById[r.category_id]?.name || "Uncategorised",
          sub: r.sub_account_id ? (accById[r.sub_account_id]?.name || "—") : "",
          months: {}, total: 0 };
      }
      map[key].months[r.month] = (map[key].months[r.month] || 0) + Number(r.amount || 0);
      map[key].total += Number(r.amount || 0);
    }
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [data, mode]);
  return (
    <div className="space-y-4" data-testid="projections-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-xs">Year</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-8 w-28" data-testid="proj-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[-1, 0, 1].map((d) => {
                const y = new Date().getFullYear() + d;
                return <SelectItem key={y} value={String(y)}>{y}</SelectItem>;
              })}
            </SelectContent>
          </Select>
          <div className="flex border border-border overflow-hidden">
            <button className={`px-3 py-1.5 text-xs ${mode === "recurring" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                     onClick={() => setMode("recurring")} data-testid="proj-mode-recurring">
              Actuals + Recurring
            </button>
            <button className={`px-3 py-1.5 text-xs border-l border-border ${mode === "trailing12" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                     onClick={() => setMode("trailing12")} data-testid="proj-mode-trailing12">
              Trailing-12 avg
            </button>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          Actuals YTD: <b className="font-mono text-foreground">{fmt(actualsTotal, "INR")}</b> ·
          Projected: <b className="font-mono text-foreground">{fmt(projectedTotal, "INR")}</b> ·
          Total: <b className="font-mono text-foreground">{fmt(grandTotal, "INR")}</b>
        </div>
      </div>

      <Card><CardContent className="p-4">
        {busy && <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>}
        {!busy && (
          <div className="w-full h-72 sm:h-96 min-h-[280px]" data-testid="proj-chart">
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 100000 ? `${(v/100000).toFixed(1)}L` : v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip formatter={(v) => fmt(v, "INR")} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {categories.map((c, i) => (
                  <Bar key={c} dataKey={c} stackId="a" fill={palette[i % palette.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent></Card>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground sticky top-0">
              <tr>
                <th className="text-left px-2 py-2 min-w-[160px]">Category</th>
                <th className="text-left px-2 py-2 hidden sm:table-cell min-w-[120px]">Sub-category</th>
                {(data?.months || []).map((m) => (
                  <th key={m} className="text-right px-2 py-2 whitespace-nowrap">{m.slice(5)}</th>
                ))}
                <th className="text-right px-2 py-2 font-bold">Total</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.length === 0 && (
                <tr><td colSpan={(data?.months?.length || 12) + 3} className="text-center text-muted-foreground py-8">No expense data for this year.</td></tr>
              )}
              {tableRows.map((r) => (
                <tr key={r.key} className="border-t border-border hover:bg-muted/30">
                  <td className="px-2 py-1.5 font-medium">
                    {r.category}
                    {r.sub && <div className="text-[10px] text-muted-foreground sm:hidden">{r.sub}</div>}
                  </td>
                  <td className="px-2 py-1.5 hidden sm:table-cell text-muted-foreground">{r.sub || "—"}</td>
                  {(data?.months || []).map((m) => {
                    const v = r.months[m];
                    const isFuture = m > (data?.current_month || "");
                    return (
                      <td key={m} className={`px-2 py-1.5 text-right font-mono ${v ? "" : "text-muted-foreground/40"} ${isFuture && v ? "italic opacity-80" : ""}`}>
                        {v ? Math.round(v).toLocaleString("en-IN") : "·"}
                      </td>
                    );
                  })}
                  <td className="px-2 py-1.5 text-right font-mono font-bold">{Math.round(r.total).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>
      <div className="text-[11px] text-muted-foreground">
        <i>Future-month values are italicised.</i> {mode === "recurring"
          ? "Projected values come from active recurring templates."
          : "Projected values are the trailing-12-month average per (category, sub-category)."}
      </div>
    </div>
  );
}


// ═══════════════════════ Nov 2026 · Settings · Category Manager ═══════════════════════

const REIMB_STATUS_META = {
  not_received: { label: "Not received", color: "bg-slate-100 text-slate-700" },
  invoice_raised: { label: "Invoice raised", color: "bg-blue-100 text-blue-700" },
  partial_received: { label: "Partial received", color: "bg-amber-100 text-amber-800" },
  fully_received: { label: "Fully received", color: "bg-emerald-100 text-emerald-800" },
  write_off: { label: "Write-off", color: "bg-red-100 text-red-700" },
  non_billable: { label: "Non billable", color: "bg-neutral-200 text-neutral-700" },
};

function SettingsTab({ wsId }) {
  const [cats, setCats] = useState([]);
  const [coa, setCoa] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} new | row
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const [c, a] = await Promise.all([
        api.get(`/finance/workspaces/${wsId}/expense-categories`),
        api.get(`/finance/workspaces/${wsId}/accounts`),
      ]);
      setCats(c.data?.categories || []);
      setCoa((a.data?.accounts || []).filter((x) => x.type === "expense"));
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);
  const parents = useMemo(() => cats.filter((c) => !c.parent_id), [cats]);
  const subsOf = (pid) => cats.filter((c) => c.parent_id === pid);
  const empty = { name: "", parent_id: "", linked_coa_account_id: "", order_index: 0, is_active: true };
  const openNew = (parent_id = "") => { setEditing({ ...empty, parent_id }); };
  const openEdit = (c) => { setEditing({ ...c, parent_id: c.parent_id || "", linked_coa_account_id: c.linked_coa_account_id || "" }); };
  const save = async () => {
    if (!editing?.name?.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: editing.name.trim(),
        parent_id: editing.parent_id || null,
        linked_coa_account_id: editing.linked_coa_account_id || null,
        order_index: Number(editing.order_index) || 0,
        is_active: editing.is_active !== false,
      };
      if (editing.id) {
        await api.patch(`/finance/workspaces/${wsId}/expense-categories/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(`/finance/workspaces/${wsId}/expense-categories`, payload);
        toast.success("Added");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"${!c.parent_id ? " (and all its sub-categories)" : ""}?`)) return;
    try {
      await api.delete(`/finance/workspaces/${wsId}/expense-categories/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  return (
    <div className="space-y-6" data-testid="settings-tab">
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold">Expense Categories</h3>
            <p className="text-xs text-muted-foreground">Taxonomy for Bills. Sub-categories are nested under a parent. Optionally link each to a Chart-of-Accounts expense account for future GL mapping.</p>
          </div>
          <Button size="sm" onClick={() => openNew()} data-testid="cat-new-parent"><Plus className="w-4 h-4 mr-1" />New category</Button>
        </div>
        <Card><CardContent className="p-0">
          <div className="divide-y divide-border">
            {parents.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No categories yet. Create one to start tagging bills.
              </div>
            )}
            {parents.map((p) => (
              <div key={p.id} className="p-3" data-testid={`cat-row-${p.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{p.name}</span>
                      {!p.is_active && <Badge variant="outline" className="text-[9px]">inactive</Badge>}
                    </div>
                    {p.linked_coa_account_id && (
                      <div className="text-[10px] text-muted-foreground">GL: {coa.find((a) => a.id === p.linked_coa_account_id)?.name || "—"}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openNew(p.id)} data-testid={`cat-add-sub-${p.id}`}><Plus className="w-3 h-3 mr-0.5" />Sub</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openEdit(p)} data-testid={`cat-edit-${p.id}`}>Edit</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => remove(p)} data-testid={`cat-del-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
                {subsOf(p.id).length > 0 && (
                  <div className="ml-5 mt-2 border-l-2 border-border pl-3 space-y-1">
                    {subsOf(p.id).map((s) => (
                      <div key={s.id} className="flex items-center justify-between gap-2" data-testid={`sub-row-${s.id}`}>
                        <div className="flex items-center gap-2 text-sm">
                          <span>↳</span>
                          <span>{s.name}</span>
                          {!s.is_active && <Badge variant="outline" className="text-[9px]">inactive</Badge>}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => openEdit(s)} data-testid={`sub-edit-${s.id}`}>Edit</Button>
                          <Button size="sm" variant="ghost" className="h-6 text-destructive" onClick={() => remove(s)} data-testid={`sub-del-${s.id}`}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent></Card>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="cat-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit" : "Add"} {editing?.parent_id ? "sub-category" : "category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={editing?.name || ""} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} data-testid="cat-name" className="h-10" /></div>
            {!editing?.parent_id && (
              <div><Label>Linked GL account (optional)</Label>
                <Select value={editing?.linked_coa_account_id || ""} onValueChange={(v) => setEditing((s) => ({ ...s, linked_coa_account_id: v }))}>
                  <SelectTrigger className="h-10" data-testid="cat-coa"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>{coa.filter((a) => !a.parent_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Order</Label><Input inputMode="numeric" type="number" value={editing?.order_index || 0} onChange={(e) => setEditing((s) => ({ ...s, order_index: e.target.value }))} className="h-10" /></div>
              <div className="flex items-end gap-2"><input type="checkbox" id="cat-active" checked={editing?.is_active !== false} onChange={(e) => setEditing((s) => ({ ...s, is_active: e.target.checked }))} /><Label htmlFor="cat-active" className="text-xs cursor-pointer">Active</Label></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !editing?.name?.trim()} data-testid="cat-save">{busy ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
